{
  "nav": {
    "dashboard": "Dashboard",
    "chats": "Chats",
    "archive": "Archive",
    "settings": "Settings",
    "studioData": "Studio Data",
    "propertyData": "Property Data",
    "expandMenu": "Expand menu",
    "collapseMenu": "Collapse menu",
    "goToDashboard": "Go to Dashboard",
    "logOut": "Sign out"
  },
  "layout": {
    "lastUpdatedJustNow": "Last updated just now",
    "lastUpdatedOneMin": "Last updated 1 min ago",
    "lastUpdatedMins": "Last updated {{count}} min ago",
    "wsOffline": "Connection lost. Refresh or wait to reconnect.",
    "refresh": "Refresh",
    "refreshing": "Refreshing…"
  },
  "settings": {
    "title": "Settings",
    "subtitle": "Manage account, team, and channels.",
    "tabAccount": "Account",
    "tabTeam": "Team Management",
    "tabChannels": "Channels",
    "tabAi": "AI Behavior",
    "logOut": "Sign out",
    "noPermissionToast": "You don't have permission to view this section.",
    "connections": {
      "email": {
        "channelLabel": "Email",
        "appliesToAllStudios": "All studios",
        "addressLabel": "Mailbox",
        "defaultStudioLabel": "Default studio for new threads",
        "statusConnected": "Connected",
        "statusReconnectRequired": "Reconnect required",
        "statusDisconnected": "Not connected",
        "statusError": "Error",
        "statusSkipped": "Skipped",
        "connect": "Connect",
        "reconnect": "Reconnect",
        "disconnect": "Disconnect",
        "reconnectBanner": "Your email connection expired. Reconnect to send and receive messages.",
        "reconnectBannerCta": "Reconnect email",
        "disconnectTitle": "Disconnect email?",
        "disconnectBody": "This revokes access to your shared mailbox. Past threads stay readable; sending is disabled until you connect again.",
        "toastDisconnected": "Email disconnected.",
        "toastDisconnectFailed": "Could not disconnect email."
      }
    }
  },
  "account": {
    "displayLanguageTitle": "Display language",
    "displayLanguageHint": "English, German, French, Italian, and Spanish apply across the app, including sign-in, registration, and onboarding.",
    "displayLanguageLabel": "Language",
    "languageUpdated": "Display language updated.",
    "emailTitle": "Email Address",
    "newEmail": "New email",
    "currentPassword": "Current password",
    "emailPasswordPlaceholder": "Required to change email",
    "save": "Save",
    "cancel": "Cancel",
    "changeEmail": "Change Email",
    "passwordTitle": "Password",
    "passwordSharedWarning": "This password is shared across your OktoGo studio app and the Management portal. Changing it here updates your sign-in on both.",
    "passwordConfirmTitle": "Change password on both platforms?",
    "passwordConfirmBody": "This changes your sign-in for both the OktoGo studio app (app.oktogo.ai) and Management, using the same email.",
    "passwordConfirmCancel": "Cancel",
    "passwordConfirmSubmit": "Yes, change password",
    "newPassword": "New password",
    "confirmNewPassword": "Confirm new password",
    "updatePassword": "Update Password",
    "changePassword": "Change Password",
    "verticalLabel": "Vertical",
    "notificationTitle": "Notification Preferences",
    "escalationAlerts": "Escalation Alerts",
    "escalationAlertsDesc": "Push when a chat is escalated for review or human takeover (always on). Escalation email with reason and chat excerpt is separate from WhatsApp session-expiry mail. An Account option to mute escalation email only is coming soon; push stays on.",
    "weeklySummary": "Weekly Summary",
    "weeklySummaryDesc": "Receive a weekly performance report",
    "connectionWarnings": "Connection Warnings",
    "connectionWarningsDesc": "Alert when a platform connection is lost",
    "notificationsSaved": "Notification preferences saved.",
    "verticalFitness": "Fitness / studio",
    "verticalStr": "Short-term rental",
    "toastCurrentPasswordForEmail": "Enter your current password to change email.",
    "toastVerificationEmailSent": "Verification email sent to new address.",
    "toastEmailUpdateFailed": "Failed to update email.",
    "toastPasswordMismatch": "Passwords don't match.",
    "toastPasswordUpdated": "Password updated successfully.",
    "toastPasswordUpdateFailed": "Failed to update password.",
    "toastVerticalUpdated": "Product vertical updated.",
    "toastVerticalUpdateFailed": "Failed to update vertical.",
    "toastPreferencesSaveFailed": "Failed to save.",
    "studioNameTitleStudio": "Studio name",
    "studioNameTitleProperty": "Property name",
    "studioNameHint": "This is how your venue appears in the app, notifications, and customer-facing messages.",
    "studioNameContextHint": "Select a single studio to edit its name.",
    "studioNameValidation": "Name must be between 2 and 60 characters.",
    "toastStudioNameUpdated": "Studio name updated.",
    "toastPropertyNameUpdated": "Property name updated.",
    "toastStudioNameUpdateFailed": "Could not update name."
  },
  "auth": {
    "languageSwitcherAria": "Display language",
    "placeholderEmailExample": "you@studio.com",
    "placeholderPassword": "••••••••",
    "placeholderPasswordCreate": "Set your password",
    "placeholderPasswordConfirm": "Repeat your password",
    "placeholderStudioName": "Your Studio Name",
    "onboardingSelectPlatform": "Select your platform",
    "onboarding2faSecret": "Enter your 2FA secret or backup code",
    "login": {
      "subtitle": "Sign in to your Studio",
      "lockout": "Too many failed attempts. Try again in {{minutes}} minutes.",
      "emailLabel": "Email address",
      "passwordLabel": "Password",
      "passwordPlaceholder": "Enter your password",
      "rememberMe": "Remember me",
      "rememberMeHelpOn": "Stay signed in on this device after closing the browser.",
      "rememberMeHelpOff": "You'll be signed out when you close the browser.",
      "logIn": "Log in",
      "forgotPassword": "Forgot password?",
      "orDivider": "or",
      "continueWithGoogle": "Continue with Google",
      "noAccount": "Don't have an account?",
      "createAccount": "Create one →",
      "sessionExpiredToast": "Your session has expired. Please log in again.",
      "pilotInviteHandoff": "Your pilot invite is accepted. Sign in with the password you just created.",
      "errorIncorrectCredentials": "Incorrect email or password.",
      "errorEmailNotVerified": "Email address is not verified.",
      "resendVerificationAction": "Resend verification email",
      "resendVerificationToast": "Verification email sent. Check your inbox.",
      "resendVerificationRateLimited": "Too many attempts. Please wait 15 minutes before requesting another verification email.",
      "goToVerificationPage": "Go to verification page →",
      "errorNetwork": "Cannot reach the server. Start the API (e.g. npm run dev or dev:server) and try again.",
      "errorGooglePopupBlocked": "A popup was blocked by your browser. Please allow popups for this site and try again.",
      "errorGoogleSignIn": "Google sign-in failed. Please try again or use email and password."
    },
    "register": {
      "subtitle": "Create your account",
      "studioNameLabel": "Studio name",
      "emailLabel": "Email address",
      "passwordLabel": "Password",
      "createAccount": "Create account",
      "orDivider": "or",
      "continueWithGoogle": "Continue with Google",
      "hasAccount": "Already have an account?",
      "logIn": "Sign in →",
      "validationStudioName": "Studio name must be at least 2 characters.",
      "validationEmail": "Enter a valid email address.",
      "validationPassword": "Password must be at least 8 characters, include 1 letter and 1 number.",
      "registrationFailed": "Registration failed. Please try again.",
      "duplicateEmailLead": "An account with this email already exists.",
      "duplicateEmailLink": "Sign in →"
    },
    "forgot": {
      "subtitle": "Reset your password",
      "emailLabel": "Email address",
      "sentLead": "Check your inbox. We've sent a password reset link to your email.",
      "backToLogin": "← Back to login",
      "testResetLink": "(Test: go to reset password page)",
      "sendResetLink": "Send reset link",
      "errorGeneric": "Something went wrong. Please try again.",
      "networkError": "Network error. Please try again."
    },
    "reset": {
      "invalidLink": "This reset link is invalid or has expired.",
      "requestNewLink": "Request a new link →",
      "tokenUsed": "This reset link has already been used.",
      "requestNewOne": "Request a new one →",
      "successBody": "Password updated. Please log in.",
      "goToLogin": "Go to login →",
      "subtitle": "Set a new password",
      "newPasswordLabel": "New password",
      "confirmPasswordLabel": "Confirm password",
      "resetButton": "Reset password",
      "testSimulateUsed": "(Test: simulate already-used token)",
      "errorPasswordRules": "Password must be at least 8 characters, include 1 letter and 1 number.",
      "errorPasswordMismatch": "Passwords do not match.",
      "errorTokenExpired": "This reset link has expired. Request a new one.",
      "networkError": "Network error. Please try again.",
      "errorGeneric": "Something went wrong."
    },
    "invite": {
      "invalidToken": "This invitation link is invalid.",
      "goToLogin": "Go to login →",
      "subtitle": "Accept your team invitation",
      "invalidUsed": "This invitation is invalid or already used.",
      "expired": "This invitation has expired.",
      "expiredHint": "Ask your studio admin to resend your invitation.",
      "successRedirect": "Invitation accepted. Redirecting to your dashboard...",
      "createPasswordLabel": "Create password",
      "termsPrivacy": "I agree to the Terms of Service and Privacy Policy.",
      "acceptButton": "Accept invitation",
      "validationPassword": "Password must be at least 8 characters and include 1 letter and 1 number.",
      "validationTerms": "You must accept Terms and Privacy to continue.",
      "errorAcceptFailed": "Could not accept invitation.",
      "errorNoSession": "Invitation accepted but login session was not created.",
      "networkError": "Network error. Please try again."
    },
    "civ": {
      "documentTitle": "Confirm your email — OktoGo",
      "headerSubtitle": "Confirming your email lets the studio continue your booking.",
      "consent": {
        "headline": "Confirm it's you for {{ studioName }}",
        "body": "This confirms you control this email and lets {{ studioName }} (powered by OktoGo.ai) take the booking action you asked for on {{ channelLabel }}.",
        "checkboxEmail": "I confirm this email address belongs to me.",
        "checkboxData": "I agree that OktoGo.ai stores and processes my personal data to fulfil this request, as described in the <a>Privacy Policy</a>.",
        "privacyPolicyAriaLabel": "OktoGo.ai privacy policy (opens in a new tab)",
        "confirmButton": "Confirm & Continue",
        "confirmButtonSubmitting": "Confirming…",
        "declineLink": "This wasn't me — decline",
        "errorGeneric": "Something went wrong. Please try again or contact support.",
        "errorRateLimited": "Too many attempts. Please wait a moment and try again.",
        "errorVersionMismatch": "We've updated this page. Please refresh and try again."
      },
      "success": {
        "headline": "You're verified.",
        "body": "You can now return to your {{ channelLabel }} chat with {{ studioName }} to continue. We'll keep going from where you left off.",
        "closeHint": "You can safely close this tab."
      },
      "error": {
        "expiredHeadline": "This link has expired",
        "expiredBody": "For your safety, the link works only for a short time. Please reply to your chat and we'll send a new one.",
        "invalidHeadline": "This link isn't valid",
        "invalidBody": "The link couldn't be recognised. Please reply to your chat to start over.",
        "usedHeadline": "Already confirmed",
        "usedBody": "This email has already been confirmed. You can return to your chat to continue.",
        "networkHeadline": "Connection problem",
        "networkBody": "We couldn't reach OktoGo. Please check your internet connection and try again.",
        "networkRetryButton": "Try again"
      },
      "declined": {
        "headline": "Got it — we won't continue.",
        "body": "We've recorded your decline. If this was a mistake, please contact support and we'll help."
      },
      "terminal": {
        "contactSupport": "Contact support",
        "contactSupportAriaLabel": "Email OktoGo support"
      }
    },
    "verify": {
      "title": "Check your inbox",
      "bodyGeneric": "We've sent a verification link to your email address. Click the link to verify your account.",
      "bodyWithEmail": "We sent a verification email to {{email}}. Click the link to verify your account.",
      "linkExpiredTitle": "Your verification link has expired.",
      "resendLink": "Resend verification email",
      "rateLimited": "Too many attempts. Please wait 15 minutes before requesting another verification email.",
      "resendButton": "Resend verification email",
      "resendButtonDone": "Verification email resent ✓",
      "footerHint": "Didn't receive it? Check your spam folder or request a new link.",
      "testExpired": "(Test: simulate expired token)"
    },
    "verifyToken": {
      "loadingTitle": "Verifying your email",
      "loadingBody": "Please wait while we confirm your account.",
      "successTitle": "Email verified",
      "successBody": "Your account is verified. You can sign in now.",
      "invalidTitle": "Invalid verification link",
      "invalidBody": "This link is not valid. Request a new verification email from the sign-in page.",
      "expiredTitle": "Verification link expired",
      "expiredBody": "This link has expired. Request a new verification email from the sign-in page.",
      "errorTitle": "Verification failed",
      "errorBody": "We could not verify your email. Please try again or request a new link.",
      "goToLogin": "Go to sign in"
    },
    "verifyEmailChange": {
      "loadingTitle": "Verifying your new email",
      "loadingBody": "Please wait while we confirm your email-change request.",
      "successTitle": "Email updated successfully",
      "successBody": "Your email address has been updated.",
      "successBodyWithEmail": "Your new sign-in email is {{email}}.",
      "invalidTitle": "Invalid email-change link",
      "invalidBody": "This email-change link is invalid or has already been used.",
      "expiredTitle": "Email-change link expired",
      "expiredBody": "This email-change link has expired. Start a new email change from Settings.",
      "errorTitle": "Could not verify email change",
      "errorBody": "Something went wrong while verifying your email change. Please try again later.",
      "goToLogin": "Go to login →"
    }
  },
  "common": {
    "search": "Search…",
    "filter": "Filter",
    "sort": "Sort",
    "apply": "Apply",
    "applyWithCount": "Apply ({{count}})",
    "reset": "Reset",
    "clearAll": "Clear all",
    "cancel": "Cancel",
    "close": "Close",
    "loading": "Loading…",
    "nameAZ": "Name A–Z",
    "nameZA": "Name Z–A",
    "moreFilters": "+{{count}} more",
    "moreFiltersAria": "{{count}} more active filters — open list",
    "additionalFilters": "Additional filters",
    "removeFilterAria": "Remove {{label}} filter",
    "loadingRoute": "Loading page…",
    "loadingContent": "Loading content…",
    "loadingTable": "Loading table…"
  },
  "dashboard": {
    "todaysStats": "Today's Stats",
    "needsAttention": "Needs Your Attention",
    "seeAllChats": "See all chats",
    "badgeEscalated": "⚡ ESCALATED",
    "badgeDraftReady": "✏ DRAFT READY",
    "allClear": "All clear! No active conversations right now.",
    "weeklySummary": "Weekly Summary",
    "conversationsByIntent": "Conversations by Intent",
    "autoVsManual": "Auto-handled vs Manual",
    "legendAiHandled": "AI handled",
    "legendManual": "Manual",
    "relativeJustNow": "Just now",
    "relativeMinsAgo": "{{count}} min ago",
    "relativeOneHourAgo": "1h ago",
    "relativeHoursAgo": "{{count}}h ago",
    "relativeOneDayAgo": "1d ago",
    "relativeDaysAgo": "{{count}}d ago",
    "intentApi": {
      "booking": "Booking",
      "rebooking": "Rebook",
      "cancellation": "Cancel",
      "info": "Info",
      "schedule": "Schedule",
      "account": "Account",
      "other": "Other"
    },
    "badgeSessionExpiring": "⏱ SESSION",
    "sessionExpiringTileWindow": "Free-form window closes {{time}}",
    "sessionBannerTitle": "WhatsApp replies may be limited soon",
    "sessionBannerBody": "One or more escalated chats are nearing the end of the free-form messaging window. Open Chats to reply while you still can.",
    "sessionBannerDismiss": "Dismiss",
    "sessionBannerOpenChats": "Open filtered chats",
    "relativeUntilShort": "{{time}} left"
  },
  "chats": {
    "loadingChats": "Loading chats…",
    "noConversations": "No conversations found.",
    "noConversationsMatchFilters": "No conversations match your search or filters.",
    "listLoadFailed": "Could not load conversations. Please try again.",
    "studioContextRequired": "Studio scope is not set correctly. Switch to a specific studio or try again.",
    "studioContextRetry": "Retry",
    "studioContextSwitchStudio": "Switch to a studio",
    "unreadConversation": "Unread conversation with {{name}}",
    "unreadConversationCount": "Unread conversation with {{name}}, {{count}} unread messages",
    "session": {
      "filterBarTitle": "Filtered: session expiring",
      "filterBarHint": "Showing threads whose messaging session is expiring soon or already expired for free-form replies.",
      "clearSessionFilter": "Show all chats",
      "filterEmpty": "No chats match the session filter in this view. Try clearing the filter or switching studio scope.",
      "bannerExpiringTitle": "WhatsApp session closing soon",
      "bannerExpiringBody": "Free-form replies are available for {{time}}. After that, Meta may require an approved template or a new customer message.",
      "bannerExpiredTitle": "WhatsApp session closed for free-form replies",
      "bannerExpiredBody": "This thread is outside the usual 24-hour customer-care window. Sending may fail unless you use an approved template or the customer messages again.",
      "composerExpiredHelp": "Free-form replies are blocked. Choose an approved message template from the draft panel above.",
      "composerClosedInstagram": "It's been more than 7 days. Ask the customer to message you again before you can reply.",
      "rowAriaExpiringWhatsApp": "WhatsApp session expiring soon",
      "rowAriaExpiredWhatsApp": "WhatsApp session expired for free-form replies",
      "rowTooltipExpiringWhatsApp": "Session closes in {{time}}",
      "rowTooltipExpiredWhatsApp": "Session expired — templates or new customer message",
      "rowAriaSessionExpiring": "Messaging session expiring soon",
      "rowAriaSessionExpired": "Messaging session expired for free-form replies",
      "rowTooltipExpiringInstagram": "Reply window closes in {{time}}",
      "rowTooltipExpiredInstagram": "Outside 24h — manual replies only, up to 7 days",
      "bannerExpiringTitleInstagram": "Reply window closing soon",
      "bannerExpiringBodyInstagram": "Replies still possible for {{time}}. Reply soon or wait for the customer to message again.",
      "bannerExpiredTitleInstagram": "Outside the 24-hour reply window",
      "bannerExpiredBodyInstagram": "Outside the 24-hour window (up to 7 days): Manual reply only, or Review the suggested reply, edit, and manually send.",
      "bannerClosedTitleInstagram": "Can't reply on Instagram",
      "bannerClosedBodyInstagram": "It's been more than 7 days. Ask the customer to message you again.",
      "headerSupportWindowInstagram": "Support window · {{time}} ago"
    },
    "customerInfo": "Customer Info",
    "customerInfoTitle": "Customer Info",
    "details": {
      "title": "Customer details",
      "show": "Show customer details",
      "hide": "Hide customer details",
      "collapseAria": "Collapse customer details panel",
      "expandAria": "Expand customer details panel",
      "resizeAria": "Resize customer details panel",
      "placeholderLoading": "Loading customer details…",
      "sectionDocuments": "Documents",
      "sectionGeneral": "General",
      "sectionStudios": "Studios",
      "sectionClasses": "Classes",
      "upcomingClassesToggle": "Nearest upcoming classes",
      "upcomingClassesEmpty": "—",
      "classesLast": "Last",
      "classesNext": "Next",
      "confirmIdentity": "Confirm identity",
      "confirmIdentityHelp": "Marks this customer as identified for this chat and unlocks blocked draft approval actions."
    },
    "composer": {
      "collapseAria": "Collapse reply composer",
      "expandAria": "Expand reply composer"
    },
    "escalated": "Escalated",
    "escalatedDraftPending": "Suggested reply ready — review before sending",
    "escalationKind": {
      "soft": "Needs review",
      "hard": "Human takeover"
    },
    "escalationFamily": {
      "human_requested": "Customer asked for a human",
      "tool_or_platform_failure": "Tool or platform failure",
      "availability_unverified": "Availability not verified",
      "no_availability": "No bookable classes",
      "account_read_failed": "Account read failed",
      "conversation_stall": "Conversation stalled",
      "low_confidence_or_scope": "Out of scope or unsure",
      "frustration": "Customer frustrated",
      "max_exchanges": "Max exchanges reached",
      "identity_verification": "Identity verification needed",
      "policy_restricted": "Restricted topic",
      "manual": "Manually escalated",
      "ai_unavailable": "AI unavailable",
      "named_set_unfinished": "Cancellation not finished",
      "cancellation_refused": "Cancellation refused"
    },
    "leadWhy": {
      "structured": "From ad",
      "inferred": "Inferred"
    },
    "campaignLeadPreparation": {
      "title": "Campaign welcome prepared",
      "closedTitle": "Campaign episode closed",
      "activeTitle": "Campaign welcome active",
      "notSent": "Not sent",
      "closed": "Closed",
      "active": "Active",
      "activeBody": "The provider accepted the campaign welcome. The episode is waiting for the customer’s pack choice.",
      "closedBody": "Campaign automation stopped for this episode.",
      "terminal": {
        "purchase_verified": "Purchase verified",
        "member_discovered": "Existing member found",
        "explicit_decline": "Customer declined",
        "manager_takeover": "Manager took over"
      },
      "awaitingPack": "Waiting for pack choice",
      "packSelected": "Pack selected: {{name}}",
      "linkReady": "Purchase link ready: {{name}}",
      "purchaseClaimUnverified": "Purchase reported · verification required",
      "bodyAuto": "The campaign facts are pinned. The welcome is queued for automatic delivery.",
      "bodyManual": "The campaign facts are pinned. Review and send the campaign welcome in the suggested reply below.",
      "modeAuto": "Automatic preparation · delivery queued",
      "modeManual": "Manual preparation · review required",
      "campaign": "{{name}} · version {{version}}",
      "campaignFallback": "Campaign · version {{version}}",
      "packs": "Prepared packs",
      "purchaseLink": "Purchase link"
    },
    "resolved": "Resolved",
    "resolvedButton": "Mark resolved",
    "resolve": "Resolve",
    "escalate": "Escalate",
    "archive": "Archive",
    "moreChatActions": "More chat actions",
    "chatIdLabel": "Chat ID",
    "loadingOlder": "Loading older messages…",
    "loadOlderMessages": "Load older messages",
    "customerFallback": "Customer",
    "senderSystem": "System",
    "loadingMessages": "Loading messages…",
    "draftBlockedDefault": "Customer must be identified before approving account actions.",
    "draftBlockedHumanHandling": "This conversation has already been handed to your team — this suggestion may be answering an earlier message.",
    "draftStaleHeadReleased": "The conversation has moved on since this reply was written.",
    "draftStaleHeadReleasedRecovery": "Edit it before sending, or reply in your own words.",
    "draftStaleHeadReleasedRefusal": "This suggested reply is out of date — the conversation has moved on since it was written. Change the wording before you send it, or reply in your own words.",
    "listJustNow": "Just now",
    "listMinsShort": "{{count}}m ago",
    "listHoursShort": "{{count}}h ago",
    "listDaysShort": "{{count}}d ago",
    "threadToday": "Today",
    "threadYesterday": "Yesterday",
    "emojiSearchPlaceholder": "Search emoji",
    "emojiSkinToneAria": "Change emoji skin tone",
    "emojiCategoryRecent": "Recent emojis",
    "emojiCategoryAll": "All emojis",
    "emojiCategorySmileys": "Smileys and people",
    "emojiCategoryPeople": "Gestures and people",
    "emojiCategoryHearts": "Hearts and celebrations",
    "emojiCategoryTravel": "Travel and places",
    "emojiCategorySymbols": "Symbols and status",
    "resolvedModalConfirm": "Mark Resolved",
    "typeMessagePlaceholder": "Type a message…",
    "editTitle": "Edit",
    "senderAi": "AI",
    "senderTeam": "Team",
    "connectPlatformHint": "Connect a booking {{listingFragment}}platform in Settings → Channels to show linked member or guest context here.",
    "connectPlatformListingFragment": "or listing ",
    "connectPlatformListingEmpty": "",
    "searchManually": "Search manually",
    "trial": "Trial",
    "firstTrial": {
      "staffHint": {
        "scenario1": "First trial — scenario 1 (no booking platform account). Staff only.",
        "scenario2": "First trial — scenario 2 (has platform account, new at your studio). Staff only."
      },
      "linksNotReady": {
        "title": "First-trial link missing",
        "reason": {
          "missing_trial_registration_link": "Add the registration / trial link before Copilot can auto-send this trial path.",
          "missing_studio_trial_booking_link": "Add the studio link before Copilot can auto-send this trial path.",
          "unknown": "Add the required first-trial links before Copilot can auto-send this trial path."
        },
        "cta": "Open General Info"
      }
    },
    "completedWriteClash": {
      "title": "Draft contradicts a completed action",
      "body": "The booking platform confirms this action completed on this turn, but the draft still tells the customer they are not entitled. Edit the reply before approving."
    },
    "groundedFailure": {
      "title": "Grounded answer needs review",
      "facts": "Facts found",
      "reason": "Verification issue"
    },
    "proposedAction": {
      "FIRST_TRIAL_LINKS_NOT_READY": "Configure trial links in Studio Data before Copilot can auto-send this trial path.",
      "GROUP_MANUAL_FULFILLMENT": "Group or team booking requires manual fulfillment by the studio team."
    },
    "campaignSend": {
      "campaign_ended": "This Campaign has ended or is no longer active, so the welcome message was not sent.",
      "instagram_window_closed": "The Instagram reply window for this customer has closed, so the welcome message was not sent.",
      "human_handling": "A team member has taken over this conversation, so the Campaign welcome message was not sent.",
      "customer_is_member": "This customer is already a member, so the Campaign welcome message was not sent.",
      "newer_customer_message": "The customer wrote again after this welcome message was prepared, so it was not sent.",
      "campaign_paused": "Campaign sending is paused for this studio right now, so the welcome message was not sent.",
      "instagram_not_connected": "The Instagram connection for this studio could not be confirmed. Check the connection in Settings, then try again.",
      "send_in_progress": "Another send for this customer is still in progress. Wait for it to finish before trying again; this can take a minute.",
      "cooldown_or_capacity": "This customer has reached the Campaign welcome limit for now, so the message was not sent.",
      "draft_changed": "This draft no longer matches the prepared Campaign message. Refresh the conversation before trying again.",
      "group_booking_active": "A group booking proposal is still open in this conversation, so the Campaign welcome message was not sent. Resolve or discard that proposal first.",
      "no_customer_message": "There is no customer message in this conversation yet, so the Campaign welcome message was not sent.",
      "internal": "The Campaign welcome message could not be sent because an internal check failed. Try again later or contact support.",
      "customer_refused": "This customer declined the Campaign, so no welcome message was sent.",
      "customer_reply_pending_check": "The latest customer reply is still being checked, so the welcome message was not sent yet. Try again shortly.",
      "campaign_updated": "The Campaign was updated after this welcome was prepared, so it was not sent yet. The message now matches the current offer. Check it, then press Send again.",
      "campaign_updated_edited": "The Campaign was updated after this welcome was prepared, so it was not sent yet. Your edited text was kept. Check the packages and prices in it, then press Send again.",
      "welcome_too_long": "This welcome is too long for one Instagram message, so it was not sent. Shorten it, then press Send again.",
      "welcome_capacity_reached": "This customer has already received the maximum of two Campaign welcomes from this studio, so the message was not sent.",
      "welcome_cooldown_active": "This customer received a Campaign welcome from this studio in the last 30 days, so the message was not sent. A new welcome is possible after the 30 days.",
      "campaign_episode_active": "A Campaign conversation with this customer is still running, so a second welcome message was not sent.",
      "campaign_episode_ended": "An earlier Campaign conversation with this customer has already ended, so the welcome message was not sent.",
      "welcome_delivery_unknown": "An earlier send of this welcome may already have reached the customer, so it was not sent again."
    },
    "confirmObject": {
      "cardTitleBooking": "Booking to confirm",
      "cardTitleCancel": "Cancellation to confirm",
      "cardTitleRebook": "Cancel and book",
      "cardTitleIdentity": "Identity to confirm",
      "customerLabel": "Customer",
      "classLabel": "Class",
      "classIdLabel": "Class id",
      "dateLabel": "Date",
      "timeLabel": "Time",
      "instructorLabel": "Instructor",
      "bookingRefLabel": "Booking ref",
      "rebookFromLabel": "Cancel",
      "rebookToLabel": "Book",
      "cancelSetAnd": "and",
      "cancelSetUnknownClass": "A class (details unavailable)",
      "contactLabel": "Contact",
      "contactMissing": "No email, phone, or Instagram handle on this chat.",
      "itineraryPartial": "Class name, date, and local time are not in this payload yet. Confirm still runs the approved write.",
      "confirmBooking": "Confirm booking",
      "confirmCancellation": "Confirm cancellation",
      "confirmRebook": "Confirm cancel and book",
      "confirmIdentity": "Confirm identity",
      "abort": "Abort",
      "abortUnavailable": "Discard is not available yet. Confirm still executes; hiding this card would not cancel the pending write.",
      "bookingInProgress": "Booking in progress…",
      "bookingStillRunning": "Still booking. You can keep working in other chats; the result will appear here.",
      "abortIdentityHint": "Does not confirm identity. No request is sent.",
      "claimLive": "{{name}} is finishing this now. Confirm and Abort are on hold.",
      "claimLiveNoName": "Another manager is finishing this now. Confirm and Abort are on hold.",
      "claimStale": "The last attempt did not finish. You can try again."
    },
    "groupBooking": {
      "listBadgeAria": "Group or team request",
      "listTooltip": "This conversation has a pending draft for a group- or team-shaped booking request.",
      "draftCardTitle": "Group / team request",
      "cardTitle": "Group / team request",
      "extractedPartySize": "Party size noted: {{count}}",
      "partySize": "Party size noted: {{count}}",
      "participantsLabel": "Participants",
      "checklistLabel": "Manager checklist",
      "checklistUnknown": "Item: {{key}}",
      "overrideReasonUnknown": "{{key}}",
      "status": {
        "collecting": "Collecting roster details",
        "awaitingManager": "Awaiting manager review",
        "awaitingApproval": "Awaiting your approval",
        "running": "Automation in progress",
        "done": "Completed",
        "failed": "Automation failed",
        "manualFallback": "Manual handling required",
        "notApplicable": "Not applicable"
      },
      "checklist": {
        "party_size": "Clarify final headcount and any age splits",
        "date_window": "Confirm preferred dates or time window",
        "offering_type": "Confirm class type, package, or private hire scope",
        "deposit_or_po": "Deposit, PO, or billing contact if applicable",
        "bill_to": "Bill-to entity and tax details if required",
        "roster_complete": "Confirm the roster is complete (names and contacts)",
        "class_slot": "Confirm class, session, or time slot",
        "book_without_product": "Record booking without linked product if needed",
        "invoice_externally": "Invoice or bill outside the platform if required",
        "terms_confirm_on_site": "Confirm terms and policies on site if needed"
      },
      "overrideReason": {
        "group_policy_overrode_auto_send": "Auto-send was overridden for this turn by your group / team policy — review before sending."
      }
    },
    "escalateModalTitle": "Escalate Conversation",
    "escalateModalBody": "This will notify the team for manual follow-up.",
    "resolvedModalTitle": "Mark as Resolved",
    "resolvedModalBody": "This conversation will be marked as resolved.",
    "filter": {
      "panelTitle": "Filter",
      "panelDescription": "Select chat filters and apply them to the conversation list.",
      "closeAria": "Close filter panel",
      "sectionStatus": "STATUS",
      "sectionIntention": "INTENTION",
      "sectionCustomerId": "CUSTOMER ID",
      "sectionChannel": "CHANNEL",
      "statusUnread": "Unread",
      "statusNeedsReview": "Needs Review",
      "statusEscalated": "Escalated",
      "statusAiHandled": "AI Handled",
      "statusResolved": "Resolved",
      "statusNeedsFollowup": "Needs Follow-up"
    },
    "sort": {
      "newest": "Newest first",
      "oldest": "Oldest first",
      "unreadFirst": "Unread first",
      "escalatedFirst": "Escalated first",
      "sessionExpiringFirst": "Session urgency",
      "byStatus": "Status"
    },
    "intentChip": {
      "newBooking": "New Booking",
      "rebooking": "Rebooking",
      "cancellation": "Cancellation",
      "generalInfo": "General Info",
      "schedule": "Schedule",
      "membership": "Membership",
      "other": "Other"
    },
    "customerId": {
      "identified": "Identified",
      "idPending": "ID Pending",
      "unmatched": "Unmatched",
      "generalInquiry": "General inquiry"
    },
    "leadBadge": {
      "label": "Lead"
    },
    "channel": {
      "instagram": "Instagram",
      "whatsapp": "WhatsApp",
      "facebook": "Facebook Messenger",
      "email": "Email"
    },
    "email": {
      "from": "From",
      "to": "To",
      "cc": "Cc",
      "authorCustomer": "Customer",
      "subject": "Subject",
      "sendFromMailbox": "Replies send from {{mailbox}}",
      "emptyConnectHint": "Connect email in Settings → Channels or from the header to see threads here.",
      "deliveryFailed": "Delivery failed",
      "showPreviousMessages": "Show previous messages",
      "hidePreviousMessages": "Hide previous messages",
      "inlineImageUnavailable": "Inline image unavailable",
      "attachmentDownloadFailed": "Could not download attachment",
      "grantExpiredBannerTitle": "Email reconnect required",
      "grantExpiredBannerBody": "Sending is disabled until you reconnect the mailbox.",
      "grantExpiredBannerCta": "Open Settings → Channels",
      "grantExpiredComposer": "Reconnect email in Settings to send messages.",
      "composer": {
        "send": "Send",
        "replyAll": "Reply all",
        "bold": "Bold",
        "italic": "Italic",
        "underline": "Underline",
        "bulletList": "Bullet list",
        "numberedList": "Numbered list",
        "link": "Insert link",
        "insertImage": "Insert image",
        "attachFile": "Attach file",
        "bodyAria": "Email message body",
        "linkPrompt": "Enter link URL",
        "linkUrlInvalid": "Enter a valid web address (e.g. example.com)",
        "imageTypeError": "Choose an image file",
        "attachmentMaxCount": "Maximum {{count}} attachments",
        "attachmentOversize": "Attachment is too large",
        "attachmentUploadFailed": "Could not upload attachment",
        "attachmentRemoveFailed": "Could not remove attachment",
        "inlineImageNotReady": "Inline image isn't ready to send. Wait if an upload is in progress, or remove the image and try again.",
        "removeAttachment": "Remove {{name}}",
        "bodyCharCount": "{{count}} / {{max}} characters"
      }
    },
    "provenance": {
      "autoAi": "Auto AI",
      "approvedAi": "Approved AI",
      "team": "Team",
      "sessionNudge": "OktoGo — session nudge"
    },
    "infoRow": {
      "phone": "Phone",
      "instagram": "Instagram",
      "email": "Email"
    },
    "handling": {
      "autoSendAll": "All topics: auto-send",
      "autoSendNone": "All topics: draft for approval",
      "autoSendMixed": "Mixed: some auto-send, some need approval",
      "teamStyleOn": "Team style on",
      "teamStyleOff": "Team style off",
      "autoLabelPrefix": "Auto label: {{label}}",
      "tooltip": "How AI handles this thread in OktoGo. Channel delivery still uses your connected WhatsApp or Instagram business account."
    },
    "archiveChatError": "Could not archive chat",
    "archiveChatSuccess": "Chat archived",
    "archiveDialogTitle": "Archive chat",
    "archiveDialogBody": "Are you sure you want to archive this chat?",
    "archiveDangerTitle": "Danger Zone",
    "archiveDangerBody": "This action is irreversible in the app. The conversation will be removed from your chat list and team members will no longer see it here. Message history is retained for compliance but may not be accessible from this interface.",
    "archiveAnyway": "Archive anyway",
    "customerWroteAgain": {
      "notice": "The customer wrote again. This confirmation is off. The assistant is reading their message."
    },
    "emptyStateTitle": "Select a conversation",
    "emptyStateSubtitle": "Choose from the list to view messages",
    "saveEdit": "Save",
    "messageEdited": "edited",
    "attachmentPhotoVideo": "Photo / Video",
    "attachmentDocument": "Document",
    "attachmentUpload": "Upload File",
    "noMessages": "No messages to display.",
    "draftApproveSend": "Send",
    "identityPendingHint": "Identity pending —",
    "identityApprovalSuccess": "Identity confirmed. You can now approve and send the draft.",
    "identityVerifiedMilestone": "Identity verified",
    "identityApprovalFailed": "We couldn’t confirm identity. Please try again in a moment.",
    "identityApprovalNotAvailable": "No linked customer profile yet. Wait for the customer to confirm or link a profile, then try again.",
    "identityInvalidState": "Identity is not pending approval for this conversation. Refresh and try again.",
    "draftEdit": "Edit",
    "draftDeliveredDiffers": "Delivered text differs from the approved draft.",
    "draftRegenerate": "Regenerate",
    "draftRegenerateTitle": "Regenerate",
    "draftAiDraft": "Suggested reply",
    "draftReviewing": "Suggested reply — reviewing",
    "draftReview": "Review",
    "composeManualReply": "Manual reply",
    "composeManualReplyPlaceholder": "Type your message manually",
    "draftSave": "Save Draft",
    "draftRegenerateFailed": "Regeneration failed.",
    "draftRegenerateFailedRetry": "Regeneration failed. Please try again.",
    "draftRegenerateWsFailed": "Draft regeneration failed.",
    "reject": "Reject",
    "reportIssue": "Report issue",
    "reportCopilotIssueMenu": "Report Copilot issue",
    "feedback": {
      "dialogTitleReject": "Reject suggested reply",
      "dialogTitleReport": "Report Copilot issue",
      "dialogDescriptionReject": "Tell us what went wrong. The suggested reply will be removed after you submit.",
      "dialogDescriptionReport": "Share what went wrong. Your suggested reply will stay so you can still edit or send it.",
      "categoryLegend": "What was the issue?",
      "categoryRequired": "Choose a category to continue.",
      "category": {
        "wrongInformation": "Wrong or outdated information",
        "wrongTone": "Wrong tone or wording",
        "shouldHaveAsked": "Should have asked me first",
        "bookingMistake": "Booking mistake",
        "misunderstood": "Misunderstood the request",
        "other": "Other"
      },
      "detailsLabel": "Details (optional)",
      "detailsPlaceholder": "What should Copilot have done differently?",
      "includeContext": "Include conversation context",
      "piiWarning": "Do not attach screenshots with customer phone numbers or payment details.",
      "wrongInformationHint": "You can update studio facts in",
      "studioDataLink": "Studio Data",
      "cancel": "Cancel",
      "submitReject": "Reject",
      "submitReport": "Submit report",
      "submitting": "Submitting…",
      "submitThanks": "Thanks — your feedback helps us improve Copilot.",
      "errorGeneric": "We couldn’t send your feedback. Please try again.",
      "errorNoPendingDraft": "There is no pending draft to reject.",
      "errorRateLimited": "You’ve sent several reports recently. Please wait a few minutes and try again.",
      "errorForbidden": "You don’t have permission to submit feedback for this chat.",
      "reportedDraftLabel": "Reported suggested reply",
      "errorAttachmentUpload": "We couldn’t upload that image. Please try again.",
      "errorUnsupportedFileType": "Unsupported file type",
      "attachmentsLabel": "Screenshots (optional)",
      "addAttachment": "Add image",
      "attachmentOversize": "Each image must be 2 MB or smaller.",
      "attachmentMaxCount": "You can attach up to 3 images.",
      "attachmentsDropHint": "Paste or drop images here (PNG, JPEG, WebP; max 2 MB each).",
      "scenarioSuggestedLegend": "Test scenario (suggested)",
      "scenarioConfidence": "Confidence: {{level}}",
      "scenarioConfidenceHigh": "High",
      "scenarioConfidenceMedium": "Medium",
      "scenarioConfidenceLow": "Low",
      "scenarioChange": "Change",
      "scenarioSkipMapping": "I'm not sure / skip mapping",
      "scenarioNoSuggestion": "No automatic scenario match for this category. Pick one manually or skip mapping.",
      "scenarioPickManual": "Pick scenario"
    },
    "whatsappTemplatePicker": {
      "title": "Select approved template",
      "aria": "Select approved template",
      "searchPlaceholder": "Search message templates…",
      "select": "Select",
      "insert": "Insert",
      "inWindowHint": "The template text will be inserted into your draft. Send it with Approve and send as usual."
    },
    "insertMessage": {
      "chooseTemplate": "Choose message template"
    },
    "messageTemplate": {
      "panelTitle": "Message template",
      "expiredEmptyTitle": "Messaging window closed",
      "expiredEmptyBody": "Choose an approved WhatsApp message template to reach this customer.",
      "editVariables": "Edit variables"
    },
    "feedbackEditor": {
      "bold": "Bold",
      "italic": "Italic",
      "bulletList": "Bullet list",
      "orderedList": "Numbered list",
      "emoji": "Insert emoji",
      "markdownHint": "Formatting uses Markdown. Use Preview to see how it will look.",
      "showPreview": "Preview",
      "hidePreview": "Edit"
    },
    "whatsappTemplate": {
      "panelTitle": "Message template",
      "panelSubtitle": "Outside the 24-hour window, only Meta-approved templates can reach the customer.",
      "loading": "Loading templates…",
      "loadError": "Could not load WhatsApp templates.",
      "retry": "Retry",
      "emptyApproved": "No approved templates for this studio. Add or approve templates in Settings → Channels.",
      "templateLabel": "Template",
      "templatePlaceholder": "Choose a template",
      "variableLabel": "Variable {{index}}",
      "variableCountError": "This template needs {{count}} variable(s).",
      "previewLabel": "Preview",
      "sendButton": "Send template",
      "sendSuccess": "Template message queued.",
      "sendError": "Could not send the template. Please try again.",
      "approveBlockedTooltip": "Free-form approve is blocked. Send an approved WhatsApp template below.",
      "suggestedLabel": "Suggested for this thread",
      "llmSuggestedHelper": "Suggested by AI for this thread.",
      "suggestionRationale": {
        "supportFollowup": "Follow-up on a recent customer message",
        "aiHandoff": "Hand off to a team member after escalation",
        "bookingCancelled": "Booking cancellation context",
        "classReminder": "Class or booking reminder",
        "bookingConfirmed": "Booking confirmation",
        "waitlist": "Waitlist spot available",
        "reengagement": "Re-engage after resolution"
      }
    },
    "draftApproveFailed": "We couldn’t approve and send that draft. Please try again in a moment.",
    "draftAbortFailed": "We couldn’t discard that pending action. Please try again in a moment.",
    "draftSaveFailed": "We couldn’t save your draft. Please try again in a moment.",
    "replySendFailed": "We couldn’t send your message, so we put it back in the box for you. Please try again in a moment.",
    "replyTooLong": "This message is too long to send. Shorten it, or attach the text as a file.",
    "replyTooLongDelta": "This message is {{over}} characters too long to send. Shorten it, or attach the text as a file.",
    "statusUpdateFailed": "We couldn’t update the conversation status. Please try again in a moment.",
    "messageEditFailed": "We couldn’t save that change. Please try again in a moment.",
    "messageDeliveryFailedTitle": "This message didn’t reach the customer",
    "messageDeliveryFailedBody": "It’s still in the thread here, but WhatsApp or Instagram blocked the send. This is often permissions, “Human Agent” review in Meta, or the 24-hour messaging window.",
    "messageDeliveryFailedOtherThread": "That was for a different chat (id starts with {{id}}). Open it from the list if you need to follow up.",
    "messageDeliveryFailedHumanAgentTitle": "Couldn't deliver this Instagram reply",
    "messageDeliveryFailedHumanAgentBody": "Instagram Human Agent isn't approved for this app yet, so this message couldn't reach the customer. Ask them to message you again to reopen free replies, or continue on WhatsApp if connected.",
    "messageDeliveryFailedProviderDetail": "What Instagram reported:",
    "messageDeliveryFailedBubble": "Didn't reach the customer",
    "actionLogExpand": "Expand AI Action Log",
    "actionLogCollapse": "Collapse AI Action Log",
    "actionLogTitle": "AI Action Log",
    "actionLogCountOne": "{{count}} action",
    "actionLogCountOther": "{{count}} actions",
    "actionLogAttribution": "by {{name}}",
    "actionLogLabels": {
      "draft_approved": "Draft approved and sent",
      "draft_edited": "Draft edited",
      "draft_edited_and_sent": "Draft edited and sent",
      "draft_regenerated": "Draft regenerated",
      "draft_aborted": "Draft aborted before sending",
      "manual_reply_sent": "Manual reply sent",
      "whatsapp_template_sent": "WhatsApp template sent",
      "auto_reply_sent": "Auto-reply sent",
      "status_changed": "Status changed",
      "identity_approved": "Identity approved",
      "faq_created": "FAQ created",
      "faq_edited": "FAQ edited",
      "faq_deleted": "FAQ deleted",
      "toggle_changed": "Setting changed",
      "menu_answered": "Menu answered"
    },
    "registrationTitle": "Registration",
    "registrationSendLink": "Send registration link",
    "registrationAssisted": "Assisted registration",
    "registrationSendLinkButton": "Send Link",
    "refundApplicable": "Still in good time — the class credit or pass should come back.",
    "refundNotApplicable": "Too close to class time — no class credit comes back.",
    "richCards": {
      "availabilitySpots": "{{spots}} spots",
      "availabilitySpotsWarning": "{{spots}} spots ⚠️",
      "bookingNoFee": "No fee charged",
      "bookingNewClient": "New client account created",
      "bookingConfirmedTitle": "Booking Confirmed",
      "bookingCreditsLine": "Credits used: {{used}} · Remaining: {{remaining}}",
      "rebookingCancelled": "Cancelled",
      "rebookingRebooked": "Rebooked",
      "policyRequiredNotice": "Required notice: {{value}}",
      "policyTimeUntilClass": "Time until class: {{value}}",
      "policyLateFee": "Late fee: {{value}}",
      "scheduleStatusOpen": "Open",
      "multiBookedTitle": "{{count}} classes booked",
      "membershipLabel": "Membership: {{name}}",
      "membershipUsedProgress": "{{used}} / {{total}} used",
      "membershipRemaining": "Remaining: {{remaining}}",
      "membershipExpires": "Expires: {{date}} ({{days}} days)",
      "membershipBookNext": "Book Next Class",
      "membershipRenew": "Renew — {{label}} {{price}}"
    },
    "customerPanel": {
      "noListingChannel": "No listing channel connected",
      "noBookingPlatform": "No booking platform connected",
      "memberCustomerId": "Member / customer ID",
      "secondaryReference": "Secondary reference",
      "noMatchingGuest": "No matching guest record",
      "noMatchingCustomer": "No matching customer record",
      "joinedLineStr": "{{line}} · {{platform}}",
      "guestNotFoundIn": "Guest not found in {{platform}}",
      "connectedPlatform": "Connected · {{platform}}",
      "eversportsCustomerId": "Eversports customer ID",
      "genericCustomerId": "{{platform}} customer ID",
      "mindbodyClientId": "Mindbody client ID",
      "bsportMemberId": "Bsport member ID",
      "partnerNetworkId": "Partner / network ID",
      "customerNotFoundIn": "Customer not found in {{platform}}"
    }
  },
  "studioData": {
    "activeStudioLimitReached": "Maximum 5 active studios. Archive a studio before creating another.",
    "readOnlyBanner": "You have read-only access to {{noun}}.",
    "noStudiosLinked": "No studios linked to this account.",
    "notConfiguredTab": "Not configured",
    "searchStudiosAria": "Search studios",
    "tabClasses": "Classes",
    "tabFaq": "FAQ",
    "tabGeneral": "General Info",
    "tabPolicies": "Policies",
    "tabPrices": "Prices",
    "tabTeam": "Team",
    "tabUsers": "Users",
    "tabCampaigns": "Campaigns",
    "readOnlyHint": "Only Owners and Managers can edit this.",
    "import": {
      "action": "Import",
      "actionEmpty": "Import from document",
      "titleFaq": "Import into FAQ",
      "titlePolicies": "Import into Policies",
      "titleGeneral": "Import into General Info",
      "tabPaste": "Paste text",
      "tabUpload": "Upload file",
      "uploadComingSoon": "File upload is coming soon. Paste your document text for now.",
      "pastePlaceholder": "Paste your handbook, FAQ page, or policy document here…",
      "pasteHint": "We will suggest structured entries. Nothing is saved until you review and apply.",
      "pasteCharCount": "{{count}} / {{max}} characters (minimum {{min}})",
      "continue": "Continue",
      "processing": "Reading your document…",
      "reviewTitle": "Review import",
      "reviewSummary": "{{faq}} FAQ · {{policies}} policies · {{general}} general fields proposed",
      "reviewSummaryGeneral": "{{general}} general fields proposed",
      "reviewSummaryFaq": "{{faq}} FAQ proposed",
      "reviewSummaryPolicies": "{{policies}} policies proposed",
      "warningsHeading": "Notes from the import",
      "reviewEmpty": "Nothing new to add from this document.",
      "addSelected": "Add selected ({{count}})",
      "back": "Back",
      "lowConfidence": "Low confidence — verify before applying",
      "conflictFaq": "Similar FAQ exists: {{question}}",
      "conflictPolicy": "Similar policy exists: {{title}}",
      "faqCapBanner": "Only {{allowed}} of {{selected}} FAQ can be added (50 max per studio)",
      "successFaq": "Added {{count}} FAQ entries",
      "successPolicies": "Added {{entries}} policies and {{overrides}} overrides",
      "successGeneral": "Updated {{count}} general info fields",
      "successMixed": "Import applied successfully",
      "applyPartial": "{{applied}} applied. {{failed}} could not be applied. Review the highlighted rows.",
      "rowApplyFailed": "This entry could not be applied. Check the value and try again.",
      "selectProposal": "Select proposal",
      "sourceExcerpt": "Source excerpt",
      "policyOverrideLabel": "Apply as override for {{section}}",
      "openingHoursStructured": "Structured opening hours will be applied",
      "generalField": {
        "description": "Description",
        "address": "Address",
        "directions": "Directions",
        "firstVisitInfo": "First-visit info",
        "facilities": "Facilities",
        "openingHours": "Opening hours",
        "phone": "Primary phone",
        "email": "Primary email",
        "website": "Website",
        "mapsLink": "Google Maps link"
      },
      "upload": {
        "dropHint": "Drag and drop a file here, or choose a file",
        "chooseFile": "Choose file",
        "removeFile": "Remove file",
        "pasteInstead": "Paste text instead",
        "extracting": "Extracting text from file…",
        "pdfDisclaimer": "PDF text extraction may miss tables or scanned pages. Review proposals carefully."
      },
      "errors": {
        "generic": "Import failed. Please try again.",
        "PREVIEW_EXPIRED": "This preview expired. Paste your text again.",
        "FAQ_LIMIT_REACHED": "FAQ limit reached for this studio.",
        "POLICY_LIMIT_REACHED": "Policy entry limit reached for this studio.",
        "VALIDATION_ERROR": "Some entries could not be applied. Check your selections.",
        "EXTRACTION_FAILED": "Could not extract content from this document.",
        "PREVIEW_TIMEOUT": "Processing took too long. Try a shorter document.",
        "RATE_LIMITED": "Too many import attempts. Please wait and try again.",
        "RATE_LIMIT_EXCEEDED": "Too many import attempts. Please wait and try again.",
        "KNOWLEDGE_IMPORT_DISABLED": "Import is temporarily unavailable.",
        "FILE_TOO_LARGE": "File is too large (max 5 MB).",
        "UNSUPPORTED_FILE_TYPE": "Unsupported file type.",
        "EXTRACTION_EMPTY": "No text could be extracted from this file.",
        "PASSWORD_PROTECTED_FILE": "This file is password-protected."
      }
    },
    "addNewUnit": "Add new studio",
    "archiveSuccess": "Studio archived.",
    "archiveError": "Failed to archive studio.",
    "notConfiguredShort": "Connect",
    "noStudiosFound": "No studios found.",
    "noSearchMatch": "No studios match your search.",
    "backToStudioList": "Back to studio list",
    "showStudioList": "Show studio list",
    "expandDetailFullWidth": "Expand studio detail to full width",
    "fullWidthTooltip": "Full width",
    "collapseDetailDefaultWidth": "Return to default width",
    "moreStudioActionsAria": "More studio actions",
    "studioIdLabel": "Studio ID",
    "archiveMenuItem": "Archive",
    "archiveStudioTitle": "Archive studio",
    "archiveStudioConfirm": "Are you sure you want to archive?",
    "dangerZone": "Danger Zone",
    "archiveStudioDangerBody": "The studio will be moved to Archive and hidden from active Studio Data lists.",
    "archiveAnyway": "Archive anyway",
    "sync": {
      "syncAll": "Sync all data",
      "syncPricing": "Sync Pricing",
      "syncClasses": "Sync Classes",
      "syncSchedule": "Sync Schedule",
      "syncTeam": "Sync Team",
      "blockLabel": "Sync {{block}}",
      "syncing": "Syncing…",
      "lastSynced": "Last synced: {{time}}",
      "error": "Sync failed. Try again.",
      "retry": "Retry"
    },
    "tabs": {
      "campaigns": {
        "intro": "When someone messages you from one of your Instagram ads, the assistant can welcome them with the offer that ad advertised. Set up your campaigns below.",
        "addCampaign": "Add campaign",
        "groupCurrent": "Current",
        "groupPast": "Past campaigns ({{count}})",
        "rowSummary": "{{packs}} packs · {{ads}} ads",
        "allAdsWord": "all",
        "statusActiveChip": "Active · ends {{date}}",
        "statusEndingSoonChip": "Ending soon · ends {{date}}",
        "statusScheduledChip": "Scheduled · starts {{date}}",
        "statusExpiredChip": "Expired · ended {{date}}",
        "statusNotReadyChip": "Not ready",
        "versionConflict": "This campaign changed in another session. Review the refreshed campaign and try again.",
        "readinessTitle": "Readiness",
        "readyChip": "Ready",
        "openAiBehaviour": "Open AI behaviour",
        "endDateMissingHelper": "When does this ad campaign end? The assistant stops welcoming new leads after that date — so you don't have to remember to switch it off.",
        "allAdsNote": "Every ad will get this offer — only choose this if all your current ads are about it.",
        "toastCreateFailed": "Failed to create campaign.",
        "toastSaveFailed": "Failed to save.",
        "loadError": "Could not load campaigns.",
        "loadErrorRetry": "Retry",
        "empty": {
          "title": "No campaigns yet",
          "body": "A campaign bundles the ads, trial packs and dates for one offer. Add one to get started."
        },
        "missing": {
          "template": "Welcome text: not yet set by OktoGo",
          "packs": "Add at least one pack",
          "pack_link": "A pack is missing a purchase link",
          "eligible_ads": "Choose which ads lead here",
          "valid_to": "End date missing",
          "ad_conflict": "Ad conflict"
        },
        "welcomeMessage": {
          "title": "Welcome message",
          "hint": "One welcome for all campaigns · the first message a new lead receives, with this campaign's packs",
          "placeholder": "Write the message a new lead receives first…",
          "tabEdit": "Edit",
          "tabPreview": "Preview",
          "tokenHint": "Keep {{packs}} in the text — it is replaced with this campaign's packs.",
          "counter": "{{current}} / {{max}}",
          "errorEmpty": "The welcome message cannot be empty.",
          "errorMissingSlot": "Add {{packs}} where the campaign's packs should appear.",
          "versionConflict": "This welcome message changed in another session. Reload before saving.",
          "translationFailed": "This welcome could not be translated into the other languages, so this version is not in use yet. Edit and save again to retry."
        },
        "addDialog": {
          "title": "Add campaign",
          "nameLabel": "Campaign name",
          "namePlaceholder": "e.g. Autumn trial offer",
          "validFromLabel": "Starts",
          "validToLabel": "Ends",
          "allAdsCheckbox": "All my Instagram ads",
          "create": "Create campaign",
          "nameRequired": "Campaign name is required.",
          "dateRangeInvalid": "The end date cannot be before the start date."
        },
        "packsCard": {
          "title": "Trial packs offered to campaign leads",
          "addPack": "Add pack",
          "empty": "No packs yet. Add one to offer campaign leads a trial.",
          "shopWide": "shop-wide pack",
          "linkFromEversports": "link from Eversports",
          "linkPasted": "link pasted",
          "noLinkYet": "class has no page link yet",
          "priceDrift": "price changed on Eversports"
        },
        "packPanel": {
          "titleNew": "New pack",
          "nameLabel": "Pack name",
          "namePlaceholder": "e.g. 2+1 20min Trial Pack",
          "nameRequired": "Pack name is required.",
          "priceLabel": "Price",
          "currencyLabel": "Currency",
          "conditionsLabel": "Conditions (optional)",
          "conditionsPlaceholder": "e.g. 3 guided 20-min ice-bath classes within 3 months",
          "linkedClassSectionTitle": "Linked class",
          "pricingOptionsTitle": "Pricing options (this class, from Eversports)",
          "pricingOptionsEmpty": "No pricing options synced for this class yet.",
          "purchaseLinkLabel": "Purchase link",
          "purchaseLinkPlaceholder": "https://www.eversports.at/e/…",
          "fromEversports": "from Eversports",
          "pasteALink": "Paste a link",
          "syncClassesAndPricing": "Sync Classes & Pricing",
          "readinessMissingLink": "⚠ This class has no page link yet.",
          "linkHostWarning": "This doesn't look like an Eversports link. Campaign packs are usually sold on your Eversports Prices page.",
          "priceDriftWarning": "Pack price changed on Eversports — review (synced {{synced}})",
          "deleteTitle": "Delete pack?",
          "deleteDescription": "This action cannot be undone.",
          "delete": "Delete"
        },
        "classPicker": {
          "linkToClass": "Link to a class",
          "shopWidePack": "Shop-wide pack — no single class",
          "searchPlaceholder": "Search your classes…",
          "loading": "Loading your classes…",
          "noMatch": "No matching class.",
          "addManually": "Can't find it? → Add the class manually",
          "hasPage": "● page",
          "noPage": "○ no page",
          "changeClass": "Change class",
          "manualNote": "entered by hand — not synced",
          "manual": {
            "nameLabel": "Class name",
            "namePlaceholder": "e.g. Pure Ice Bath",
            "linkLabel": "Class link (Eversports class page)",
            "linkPlaceholder": "https://www.eversports.at/e/…",
            "helper": "Open the class on Eversports and paste its page address (…/e/… or …/activity/…)."
          }
        },
        "adsCard": {
          "title": "Which ads lead here",
          "radioListed": "Only the ads I list below",
          "radioAll": "All my Instagram ads",
          "addAd": "Add ad",
          "adIdLabel": "Meta Ad ID",
          "adIdHelpAria": "What is a Meta Ad ID?",
          "adIdTooltipWhy": "This is how we recognise people who arrive from this ad, so they get this campaign's welcome.",
          "adIdTooltipWhere": "Copy the Ad ID from the Ad ID column in Meta Ads Manager — or paste the ad's Ads Manager link straight into this field.",
          "adIdPlaceholder": "e.g. 120212345678901234",
          "labelOptional": "Label (optional)",
          "labelPlaceholder": "e.g. Summer trial ad",
          "empty": "No ads listed yet.",
          "delete": "Remove ad",
          "edit": "Edit ad",
          "formatError": "Enter the ad's number, or paste the ad's Meta Ads Manager link.",
          "campaignLinkError": "That link points to a campaign, not a single ad. Open the ad itself in Ads Manager and paste that link.",
          "adsetLinkError": "That link points to an ad set, not a single ad. Open the ad itself in Ads Manager and paste that link.",
          "multipleAdsError": "That link holds more than one ad. Please add one ad at a time.",
          "duplicateError": "This ad id is already listed.",
          "conflictError": "Already used by \"{{name}}\" (until {{date}}).",
          "conflictErrorOpenEnded": "This ad is held by “{{name}}” without an end date. Set an end date on the holding Campaign.",
          "wildcardConflictErrorOpenEnded": "All ads are held by “{{name}}” without an end date. Set an end date on the holding Campaign.",
          "wildcardConflictError": "\"{{name}}\" already claims all ads in an overlapping window (until {{date}}).",
          "helperTitle": "How do I find my Meta Ad ID?",
          "helperStep1": "Open Meta Ads Manager → Ads tab.",
          "helperStep2": "Add the \"Ad ID\" column (Columns → Customise) — or open the ad and paste its whole address here; we'll pick out the number after \"selected_ad_ids=\".",
          "helperStep3": "Paste the number here — or paste the ad's Ads Manager link and we'll pick out the number.",
          "learnMore": "Learn more",
          "notSureHint": "Not sure? Choose 'All my Instagram ads' for now — you can narrow it later."
        }
      },
      "faq": {
        "saving": "Saving…",
        "saved": "Saved ✓",
        "addFaq": "Add FAQ",
        "readOnly": "Read-only",
        "questionPlaceholder": "Question",
        "answerPlaceholder": "Answer",
        "deleteTitle": "Delete FAQ?",
        "deleteDescription": "This action cannot be undone.",
        "delete": "Delete",
        "empty": "No FAQs yet. Add your first one above.",
        "toastAdded": "FAQ added.",
        "toastAddFailed": "Failed to add FAQ.",
        "toastUpdated": "FAQ updated.",
        "toastUpdateFailed": "Failed to update FAQ.",
        "toastDeleted": "FAQ deleted.",
        "toastDeleteFailed": "Failed to delete FAQ."
      },
      "general": {
        "openingHours": "Opening Hours",
        "closed": "Closed",
        "location": "Location",
        "description": "Description",
        "descriptionPlaceholder": "Short overview for customers and the AI—what you offer, vibe, and practical details like parking.",
        "address": "Address",
        "addressPlaceholder": "e.g. 42 Main Street, 10115 Berlin",
        "addressInvalid": "Enter an address with letters, a number, and a space or comma.",
        "timezone": "Studio timezone",
        "timezonePlaceholder": "e.g. Europe/Berlin",
        "timezoneHelper": "Used for this studio's schedules and time-sensitive actions.",
        "timezoneInvalid": "Enter a valid IANA timezone, such as Europe/Berlin.",
        "hoursRequired": "Set an opening and closing time for {{day}}, or mark it Closed.",
        "hoursOutOfOrder": "The closing time for {{day}} must be after the opening time.",
        "mapsLink": "Google Maps Link",
        "mapsLinkPlaceholder": "https://maps.google.com/…",
        "mapsLinkInvalid": "Enter a Google Maps link.",
        "directions": "Directions",
        "directionsPlaceholder": "How to find you—entrance, floor, transit, parking, or other arrival tips.",
        "contacts": "Contacts",
        "phone": "Primary phone",
        "phonePlaceholder": "+49 30 1234567",
        "phoneInvalid": "Enter a phone number with at least 6 digits. Use digits and standard punctuation only.",
        "email": "Primary email",
        "emailPlaceholder": "hello@yourstudio.com",
        "emailInvalid": "Enter a valid email address.",
        "website": "Website",
        "websitePlaceholder": "yourstudio.com",
        "facilities": "Facilities",
        "facilityPlaceholder": "Add facility…",
        "add": "Add",
        "firstVisit": "First-Visit Info",
        "firstVisitLinks": {
          "trialRegistrationLabel": "Trial registration link (Scenario 1)",
          "trialRegistrationPlaceholder": "yourstudio.com/trial-registration",
          "trialRegistrationHelper": "Used when the customer has no booking platform account yet.",
          "studioLabel": "Eversports studio page (Scenario 2)",
          "studioPlaceholder": "www.eversports.com/s/your-studio",
          "studioHelper": "Used when the customer has an Eversports account but is new at your studio. Prefer Overview below for general studio info links.",
          "overviewLabel": "Overview page",
          "overviewPlaceholder": "www.eversports.com/s/your-studio-slug",
          "overviewHelper": "Public Overview section (about, address, amenities). Used for general studio questions.",
          "classesLabel": "Classes calendar",
          "classesPlaceholder": "www.eversports.com/scl/your-studio-slug",
          "classesHelper": "Public Classes week calendar. Used when customers ask what’s on or how to book.",
          "pricesLabel": "Prices page",
          "pricesPlaceholder": "www.eversports.com/sp/your-studio-slug",
          "pricesHelper": "Public Prices / products catalog. Used for membership and product price questions.",
          "urlInvalid": "Add the website address, for example rowinc.studio."
        },
        "firstVisitPlaceholder": "What new customers should know before their first visit—arrival, what to bring, check-in, and studio rules.",
        "saveChanges": "Save Changes",
        "toastSaved": "Changes saved.",
        "toastSaveFailed": "Failed to save.",
        "weekday": {
          "monday": "Monday",
          "tuesday": "Tuesday",
          "wednesday": "Wednesday",
          "thursday": "Thursday",
          "friday": "Friday",
          "saturday": "Saturday",
          "sunday": "Sunday"
        }
      },
      "generalPublic": {
        "heading": "Public studio info",
        "syncLabel": "Sync General",
        "syncCaption": "Syncing General refreshes your public About, amenities, Instagram, and rating together.",
        "previewCaption": "Preview data — not real synced studio info",
        "empty": "Not synced yet. Click \"Sync General\" to preview sample public info.",
        "aboutHeading": "About",
        "amenitiesHeading": "Amenities",
        "amenityOffered": "Offered",
        "amenityNotOffered": "Not offered",
        "amenityUnknown": "Not yet synced",
        "ratingLabel": "{{value}} ({{count}} reviews)"
      },
      "policies": {
        "toastSaved": "Policy override saved.",
        "toastSaveFailed": "Failed to save.",
        "toastAdded": "Policy added.",
        "toastAddFailed": "Failed to add policy.",
        "toastUpdated": "Policy updated.",
        "toastUpdateFailed": "Failed to update policy.",
        "toastDeleted": "Policy deleted.",
        "toastDeleteFailed": "Failed to delete policy.",
        "syncedFrom": "Synced from {{source}}",
        "bookingPlatformFallback": "Booking platform",
        "manualEntriesTitle": "Custom policies",
        "addPolicy": "Add policy",
        "policyTitlePlaceholder": "Policy title",
        "policyContentPlaceholder": "Enter policy details…",
        "manualEntriesEmpty": "No custom policies yet.",
        "deleteTitle": "Delete policy?",
        "deleteDescription": "This policy entry will be removed.",
        "delete": "Delete",
        "ownerOverride": "Owner Override",
        "overridePlaceholder": "Enter your own policy text to override the synced version…",
        "saveOverride": "Save Override",
        "overrideActive": "Owner Override (Active)",
        "refundIfCancelledAtLeast": "Refund if cancelled at least",
        "beforeClassStart": "before class start",
        "hoursBeforeClassStart": "hours before class start",
        "hoursPreset": "{{count}} hours",
        "hoursCustom": "Custom…",
        "toastInvalidHours": "Enter a whole number of hours between 1 and 168.",
        "customHoursAria": "Custom hours",
        "hoursInfoAria": "More about this rule",
        "hoursInfoTitle": "About this number",
        "hoursInfoCredit": "Class credit or pass back — not a bank refund.",
        "hoursInfoMove": "Same hours when they ask to move that class.",
        "hoursInfoNumberBeatsNotes": "Copilot follows this number, not the notes below.",
        "hoursPreview": "Customers who cancel {{hours}} hours or more before class start can get a class credit or pass back. Later than that: they can still cancel or move, no credit.",
        "additionalNotes": "Additional cancellation notes (optional)",
        "additionalNotesHint": "Exceptions, illness, membership notice — Copilot may explain these. They do not change the hours above.",
        "additionalNotesPlaceholder": "Enter extra policy notes…",
        "saveAdditionalNotes": "Save",
        "additionalNotesActive": "Additional notes",
        "empty": "No policy sections yet. Connect a booking platform to sync policy text.",
        "syncCancellationPolicy": "Sync cancellation policy",
        "syncRefundPolicy": "Sync refund policy",
        "cancellationPolicyEmpty": "Not published publicly yet — nothing invented. Click \"Sync cancellation policy\" to check again.",
        "refundPolicyEmpty": "Not published publicly yet — nothing invented. Click \"Sync refund policy\" to check again.",
        "refundPolicyPreviewLine": "Preview data — not real imported policy text: \"Full refund up to 24 hours before class start; store credit after that.\""
      },
      "prices": {
        "syncCaption": "Syncing Pricing refreshes this tab and each class's Pricing options together.",
        "previewCaption": "Preview data — not live Eversports pricing",
        "membershipsHeading": "Memberships",
        "membershipsEmpty": "Not synced yet. Click \"Sync Pricing\" to preview sample memberships.",
        "productsHeading": "Products",
        "productsEmpty": "Not synced yet. Click \"Sync Pricing\" to preview sample products.",
        "minRuntime_one": "{{count}} month minimum",
        "minRuntime_other": "{{count}} months minimum"
      },
      "team": {
        "syncCaption": "Syncing Team refreshes this tab with the instructor directory from your public Eversports page.",
        "previewCaption": "Preview data — not a real instructor roster",
        "heading": "Instructors",
        "empty": "Not synced yet. Click \"Sync Team\" to preview sample instructors.",
        "viewSchedule": "View schedule"
      },
      "users": {
        "selectStudio": "Select a studio to manage users.",
        "peopleWithAccessLead": "People with access to",
        "peopleWithAccessTrail": ".",
        "thisStudio": "this studio",
        "addMember": "Add member",
        "inviteTitle": "Invite team member",
        "email": "Email",
        "managerInviteShort": "Managers can invite Staff only.",
        "sendInvitation": "Send invitation",
        "revokeHintAddMember": "You can send a new invitation later from Add member if you change your mind.",
        "emptyTeam": "No team members or pending invitations yet."
      },
      "sidePanel": {
        "title": "Add New Studio",
        "studioName": "Studio name",
        "namePlaceholder": "Enter studio name",
        "nameRequired": "Studio name is required.",
        "nameTooShort": "Studio name must be at least 2 characters.",
        "nameTooLong": "Studio name must be at most 60 characters.",
        "nameAlreadyExists": "Studio name already exists.",
        "nameArchivedConflict": "This name matches an archived studio. Restore or rename the archived studio first.",
        "studioLimitReached": "Maximum 5 active studios. Archive a studio before creating another.",
        "description": "Description",
        "descriptionPlaceholder": "Enter studio description",
        "descriptionTooLong": "Description must be at most 200 characters.",
        "descriptionCharCount": "{{current}}/{{max}}",
        "teamMembers": "Team members",
        "teamHint": "Invite people after the studio is created, or add names and emails below to invite automatically.",
        "memberNamePlaceholder": "Full name",
        "memberNameRequired": "Name is required.",
        "memberNameAria": "Invitee name",
        "memberEmailAria": "Invitee email",
        "emailPlaceholder": "email@example.com",
        "emailRequired": "Email is required.",
        "emailInvalid": "Enter a valid email address.",
        "emailDuplicate": "That email is already in the list.",
        "addMemberButtonAria": "Add team member to list",
        "editMemberAria": "Edit team member",
        "saveMemberEdit": "Save member",
        "finishEditingFirst": "Save or cancel your edits before creating the studio.",
        "removeAria": "Remove",
        "cancel": "Cancel",
        "create": "Create Studio",
        "toastInvalidEmail": "Enter a valid email.",
        "toastDuplicateEmail": "That email is already in the list.",
        "toastCreateFailed": "Failed to create studio.",
        "toastInviteFailed": "Invitation to {{email}} could not be sent.",
        "toastCreated": "Studio created.",
        "toastGrantFailedExisting": "Could not grant access to {{email}} for this studio.",
        "toastGrantAlreadyMemberExisting": "{{email}} is already a member of this new studio."
      },
      "trace": {
        "panelTitle": "Vision agent trace",
        "closeAria": "Close",
        "enterFullscreenAria": "Expand to full screen",
        "exitFullscreenAria": "Exit full screen",
        "backToClasses": "Back to Classes",
        "pageTitle": "Vision agent trace",
        "pageSubtitle": "Pick a recent run from the list (ClickHouse audit) or paste a run ID. S3 is required for screenshots and artifact downloads.",
        "selectStudioForTraces": "Select a studio to load traces.",
        "recentRuns": "Recent agent runs",
        "window": "Window",
        "daysOption": "{{count}} days",
        "refresh": "Refresh",
        "clickHouseNoList": "No agent runs found in this window. ClickHouse audit is not configured, so this list comes from the local Postgres audit log. Use “Run ID” below if you have a UUID.",
        "loadingRuns": "Loading runs…",
        "lastActivity": "Last activity",
        "platform": "Platform",
        "lastEvent": "Last event",
        "events": "Events",
        "estimatedLlmCostColumn": "Est. LLM cost",
        "runsListTotalEstimatedLlmCost": "Total for runs shown (LLM est.): {{amount}}",
        "runsListCostDisclaimer": "Amounts are in-app estimates from token usage (list-price heuristics), not invoices.",
        "runIdLabel": "Run ID (optional)",
        "runIdSrOnly": "Agent run ID",
        "runIdPlaceholder": "Paste UUID if not in list above",
        "loadTrace": "Load trace",
        "loadError": "Could not load trace.",
        "syncComplete": "Sync Complete",
        "syncFailed": "Sync Failed",
        "runLoaded": "Run Loaded",
        "linkPlaywrightTrace": "Playwright trace",
        "linkRecording": "Recording",
        "linkHar": "HAR",
        "linkLlmDecisions": "LLM decisions",
        "latestStepAlt": "Latest step",
        "classesSynced": "{{count}} classes synced",
        "percentComplete": "100%",
        "llmTotalLabel": "LLM total:",
        "llmCallsCount": "({{count}} calls)",
        "failureHeading": "Failure screenshot",
        "failureAlt": "Failure",
        "s3NotConfigured": "S3 agent traces are not configured on this server; screenshots and downloads are unavailable.",
        "durationMs": "{{n}} ms",
        "durationSeconds": "{{n}}s",
        "runDurationSuffix": " · {{n}}s",
        "tokensSummary": "{{input}} in / {{output}} out ({{total}} tokens)",
        "orchestratorTraceHeading": "Chat orchestrator trace",
        "intent": "Intent",
        "confidence": "Confidence",
        "decisionOrigin": "Decision",
        "groundingVerdict": "Grounding",
        "escalationReason": "Escalation",
        "toolsCalled": "Tools",
        "toolOutcome": "Tool outcome",
        "ragChunks": "RAG",
        "decisionSteps": "Decision steps",
        "llmTemperature": "temp {{value}}",
        "llmMaxTokens": "max {{value}} tok",
        "expandDetails": "Details",
        "collapseDetails": "Hide",
        "executionRoute": "Route",
        "interactionMode": "Mode",
        "subFlow": "Sub-flow",
        "method": "Method",
        "selfHealed": "Self-healed",
        "systemPrompt": "System",
        "userPrompt": "User",
        "llmResponse": "Response",
        "llmPromptKey": "Prompt",
        "llmPromptSummary": "Structure",
        "llmReasoning": "Reasoning",
        "promptKind": "Kind",
        "workflowLiveBadge": "Live",
        "workflowReplayBadge": "Replay",
        "workflowTopologyTitle": "Workflow topology",
        "workflowEventsCount": "{{count}} live events",
        "cascadeOf": "Cascade of root failure {{rootCauseId}}",
        "technicalDetails": "Technical details",
        "openConversation": "Open conversation",
        "privacyHiddenContent": "(content hidden for privacy)",
        "tier": {
          "decisionLabel": "Tier decision",
          "name": {
            "json_feed": "JSON feed",
            "dom_text_early": "DOM text (early)",
            "dom_script": "DOM script",
            "cli_playwright": "CLI Playwright",
            "mcp_text": "MCP text",
            "vision_agent": "Vision agent"
          },
          "outcome": {
            "success": "Success",
            "fail": "Fail",
            "skip": "Skip"
          },
          "reason": {
            "skip_anonymous_or_non_schedule": "Skipped (anonymous / non-schedule)",
            "skip_dom_not_applicable": "Skipped (DOM not applicable)",
            "disabled_by_config": "Disabled by config",
            "consecutive_failures": "Consecutive failures",
            "llm_last_resort_enabled": "LLM last-resort enabled",
            "range_mismatch": "Range mismatch",
            "empty_feed": "Empty feed",
            "tier_timeout": "Tier timeout",
            "tier_error": "Tier error",
            "selector_not_found": "Selector not found",
            "initial_navigation_failed": "Initial navigation failed",
            "auth_retry_limit": "Auth retry limit reached",
            "reauth_failed": "Re-authentication failed",
            "no_meaningful_result": "No meaningful result"
          }
        }
      },
      "classes": {
        "weekdayMon": "Mon",
        "weekdayTue": "Tue",
        "weekdayWed": "Wed",
        "weekdayThu": "Thu",
        "weekdayFri": "Fri",
        "weekdaySat": "Sat",
        "weekdaySun": "Sun",
        "viewList": "List",
        "viewCalendar": "Calendar",
        "syncComplete": "Sync Complete",
        "syncFailed": "Sync Failed",
        "syncing": "Syncing…",
        "viewTrace": "View trace",
        "latestStepAlt": "Latest step",
        "agentScreenshots": "Agent Screenshots ({{count}})",
        "stepLabel": "Step {{n}}",
        "tableClass": "Class",
        "tableInstructor": "Instructor",
        "tableTime": "Time",
        "tableDuration": "Duration",
        "tableLevel": "Level",
        "tableSpots": "Spots in next class",
        "minutes": "{{n}} min",
        "spotsLeft": "{{n}} left",
        "full": "Full",
        "emptyList": "No classes synced yet. Connect a booking platform and run a sync.",
        "detailStudio": "Studio",
        "detailLocation": "Location",
        "detailTrainer": "Trainer",
        "detailTime": "Time",
        "detailCapacity": "Capacity",
        "detailDuration": "Duration",
        "about": "About",
        "descriptionEmpty": "No class description synced yet.",
        "pricingOptions": "This class's bookable options",
        "pricingOptionsEmpty": "Pricing options not available yet. Copilot will not invent prices.",
        "links": "Links",
        "classPageLink": "Open class page",
        "bookingLink": "Open booking link",
        "bookSlot": "Book",
        "clients": "Clients ({{count}})",
        "bookingConfirmed": "Confirmed",
        "bookingWaitlisted": "Waitlisted",
        "bookingCancelled": "Cancelled",
        "bookingCheckedIn": "Checked In",
        "upcomingSchedule": "Upcoming Schedule",
        "noUpcomingSessions": "No upcoming sessions in this period.",
        "showNextMonth": "Show next month",
        "showNextMonthAria": "Show next month",
        "loading": "Loading…",
        "toastSyncComplete": "Sync completed successfully.",
        "toastSyncFailed": "Sync failed.",
        "toastSyncBookingPlatformNotConfigured": "Connect a booking platform (Eversports, Mindbody, or Bsport) before syncing.",
        "syncLogInit": "Starting sync for {{start}} → {{end}}…",
        "syncLogStarted": "Sync job started on server",
        "syncLogScreenshots": "Loaded {{count}} agent screenshots",
        "syncLogCompleted": "Sync completed",
        "syncLogFailedMsg": "Sync failed",
        "syncStartError": "Failed to start sync"
      }
    }
  },
  "studioDataFilter": {
    "filter": "Filter",
    "clearAll": "Clear all",
    "panelTitle": "Filter",
    "panelDescription": "Narrow the studio list by integrations, booking source, and location.",
    "closeSheetAria": "Close",
    "sheetAriaLabel": "Filter studios",
    "channelConnection": "{{channel}} — connection",
    "sectionBookingSource": "Booking source",
    "sectionLocation": "Location (city, country)",
    "channelInstagram": "Instagram",
    "channelWhatsapp": "WhatsApp",
    "channelBooking": "Booking",
    "tagChannelStatus": "{{channel}}: {{status}}",
    "tagSource": "Source: {{source}}",
    "connectionStatus": {
      "connected": "Connected",
      "error": "Error",
      "not_connected": "Not connected"
    },
    "bookingSource": {
      "eversports": "Eversports",
      "mindbody": "Mindbody",
      "bsport": "Bsport",
      "none": "None",
      "other": "Other"
    }
  },
  "team": {
    "selectStudioHint": "Select a studio in the header switcher to view the team list, remove members, and use Invite again for pending invites.",
    "aggregateListHint": "Team members and pending invites are listed for every studio you can access. Remove and resend apply to the studio shown in each row.",
    "inviteMember": "Invite Team Member",
    "studiosMultiLabel": "Studios (select one or more)",
    "studioSingleLabel": "Studio",
    "noStudiosForInvite": "No studios are available for invitations with your account.",
    "includeStudioAria": "Include {{name}}",
    "emailAddress": "Email address",
    "inviteNamePlaceholder": "Enter name",
    "emailPlaceholder": "team@studio.com",
    "role": "Role",
    "roleManager": "Manager",
    "roleStaff": "Staff",
    "managerInviteHint": "Managers can invite Staff only (per studio access you manage).",
    "accessPreview": "They will get access to {{studios}} as {{role}}.",
    "roleOwner": "Owner",
    "sendInvitation": "Send Invitation",
    "colName": "Name",
    "pendingInviteNoName": "—",
    "pendingInviteNameSr": "Pending invitation for {{email}}",
    "emailInvalid": "Enter a valid email address.",
    "colStudio": "Studio",
    "colEmail": "Email",
    "colRole": "Role",
    "colStatus": "Status",
    "colActions": "Actions",
    "statusActive": "Active",
    "statusPending": "Pending",
    "ownerCannotRemove": "Studio owners cannot be removed here",
    "inviteAgain": "Invite again",
    "inviteToOtherStudios": "Invite to other studios",
    "editAccess": "Edit access",
    "editAccessLoadingStudios": "Loading studios where this member has access…",
    "editAccessPerStudioHint": "Choose a role for each studio. Only studios you can manage are listed.",
    "editAccessNothingChanged": "No changes to save.",
    "editAccessStudioRoleAria": "{{studio}} — role",
    "editAccessMemberDetail": "{{name}} · {{email}}",
    "removeMemberMultiHint": "Choose every studio you want to remove this person from. Unchecked studios keep them as a member.",
    "removeMemberLoadingScope": "Checking your studios…",
    "removeMemberStudioToggleAria": "Remove from {{name}}",
    "removeMemberPickOneStudio": "Select at least one studio.",
    "toastMemberRemovedMulti": "Removed from {{count}} studios.",
    "toastRemovePartialFail": "Could not remove from: {{studios}}. Try again for those studios.",
    "resendInvitation": "Resend invitation",
    "editPendingInviteAria": "Edit pending invitation for {{email}}",
    "editPendingInviteTitle": "Edit pending invitation",
    "editPendingInviteSave": "Save changes",
    "toastInviteUpdated": "Invitation updated.",
    "toastAccessUpdated": "Member access updated.",
    "toastEditAccessPartialFail": "Could not update access for: {{studios}}. Try again for those studios.",
    "toastInviteUpdatePartial": "Old invitation revoked, but resending failed. Use Add member to invite again.",
    "removeMemberAria": "Remove {{name}} from studio",
    "revokeInviteAria": "Revoke invitation",
    "removeMemberTitle": "Remove team member",
    "removeMemberBody": "Are you sure you want to remove this person from the studio?",
    "removeMemberDetail": "{{name}} ({{email}}) will lose access to this studio immediately.",
    "removeMemberDetailStudio": "{{name}} ({{email}}) will lose access to {{studio}} immediately.",
    "dangerIrreversible": "This action is irreversible from here. Their OktoGo account is not deleted; you can invite them again later if needed.",
    "removeAnyway": "Remove anyway",
    "revokeTitle": "Revoke invitation",
    "revokeBody": "The pending invite to {{email}} will be cancelled. They will not be able to join with the current link.",
    "revokeHint": "You can send a new invitation later from Invite Team Member if you change your mind.",
    "revoke": "Revoke",
    "toastInviteSent": "Invitation sent.",
    "toastInviteFailed": "Failed to send invitation.",
    "toastAlreadyMember": "{{email}} is already a member of this studio. Open the team list below to manage their role.",
    "toastAlreadyMemberMulti": "{{email}} is already a member of one of the selected studios. Uncheck that studio and try again.",
    "addMemberSearchPlaceholder": "Add by name or email",
    "addMemberSearchHint": "Pick someone from your studios, or type a new email to invite.",
    "candidateSelectBeforeInvite": "{{email}} already belongs to an existing user. Select that user from the list to grant access.",
    "candidateSearching": "Searching…",
    "candidateNoMatches": "No matching teammates in your studios.",
    "candidateNoMatchHelp": "We don't recognise this email yet — we'll send an invitation.",
    "candidateAlreadyMemberBadge": "Already member",
    "candidateChange": "Change",
    "candidateAlreadyInLabel": "Already in: {{studios}}",
    "refineSearch": "Refine your search.",
    "candidateAlreadyMemberHere": "{{name}} is already a member of this studio.",
    "grantAccessTitle": "Grant access",
    "grantAccessButton": "Grant access",
    "grantAccessNotice": "{{name}} will see this access the next time they open OktoGo.",
    "toastGrantSuccess": "Access granted to {{name}}.",
    "toastGrantFailed": "Failed to grant access.",
    "toastMemberRemoved": "Member removed from this studio.",
    "toastRemoveFailed": "Failed to remove member.",
    "toastResent": "Invitation sent again.",
    "toastResendFailed": "Failed to resend.",
    "toastResendRateLimited": "Invitation was just resent. Try again in a few minutes.",
    "toastRevoked": "Invitation revoked.",
    "toastRevokeFailed": "Failed to revoke."
  },
  "connections": {
    "addChannel": "Add Channel",
    "addBookingPlatform": "Add booking platform",
    "mockModeBanner": "Mock mode — channels are simulated, no real Meta or booking platform connections.",
    "mockReconnectHint": "In mock mode, use Reconnect to refresh simulated WhatsApp status.",
    "errors": {
      "connectionIssue": "Connection issue",
      "activationRequired": "WhatsApp activation required — enter your 6-digit PIN in Settings"
    },
    "colChannel": "Channel",
    "colIdentifier": "Identifier",
    "colAppliesTo": "Applies to",
    "colStatus": "Status",
    "colActions": "Actions",
    "statusConnected": "Connected",
    "statusError": "Error",
    "statusSkipped": "Skipped",
    "statusNotConnected": "Not connected",
    "connectedSince": "Connected since {{date}}",
    "connectedSinceLabel": "Connected since",
    "lastSynced": "Last synced: {{time}}",
    "configure2fa": "Configure 2FA",
    "reconnect": "Reconnect",
    "connect": "Connect",
    "emptyChannels": "No channels configured. Add one to get started.",
    "modalAddTitle": "Add Channel",
    "channelType": "Channel Type",
    "addChannelTenantScopeHint": "Messaging channels apply to all studios in your account. You are not assigning a channel to a single studio.",
    "addChannelSubmit": "Add Channel",
    "bookingReconnectTitle": "Reconnect Booking Platform",
    "bookingConnectTitle": "Connect Booking Platform",
    "bookingTenantScopeHint": "Booking platform credentials apply to all studios in your account. You are not limiting this integration to a single studio.",
    "twoFaTenantScopeHint": "Two-factor authentication for booking applies at account level for Eversports. It covers all studios that use this booking connection.",
    "platformLabel": "Platform",
    "bookingPlatformLabel": "Booking Platform",
    "selectPlatformPlaceholder": "Select your platform",
    "usernameEmail": "Username / Email",
    "usernamePlaceholder": "you@studio.com",
    "password": "Password",
    "passwordPlaceholder": "••••••••",
    "updateCredentials": "Update credentials",
    "twoFaModalTitle": "Configure Eversports 2FA",
    "twoFaMethod": "Method",
    "twoFaTotp": "TOTP (Authenticator app)",
    "twoFaEmail": "Email code",
    "twoFaSms": "SMS code",
    "twoFaSecretLabel": "Secret / code",
    "twoFaContactLabel": "Phone or email",
    "twoFaSecretPlaceholder": "Paste TOTP secret or code",
    "twoFaContactPlaceholder": "Phone number or email",
    "twoFaCodeLabel": "Authenticator key",
    "twoFaCodePlaceholder": "Paste your TOTP authenticator secret key",
    "twoFaCodeHint": "Find this key in your authenticator app setup (e.g. Google Authenticator, Authy). The code will be generated automatically.",
    "save": "Save",
    "toastStartFlowFailed": "Could not start connection flow.",
    "toastReconnectStarted": "Reconnect flow started.",
    "toastReconnectFailed": "Reconnect failed.",
    "toastDisconnected": "Disconnected.",
    "toastDisconnectFailed": "Disconnect failed.",
    "toastBookingCredentialsUpdated": "Booking platform credentials updated.",
    "toastBookingConnected": "Booking platform connected.",
    "toast2faConfigured": "2FA configured.",
    "errorUserPassRequired": "Username and password are required.",
    "errorSelectPlatform": "Please select a booking platform.",
    "errorInvalidCredentials": "Invalid credentials or platform unavailable. Please retry.",
    "errorGenericRetry": "Something went wrong. Please retry.",
    "error2faSecretRequired": "Secret or code is required.",
    "error2faCodeRequired": "Authenticator key is required for Eversports.",
    "errorUpdateFailed": "Update failed.",
    "errorConnectFailed": "Connect failed.",
    "errorConfigure2faFailed": "Failed to configure 2FA.",
    "errorNoStudioForApi": "No studio is available for this account. Add a studio before managing connections.",
    "channelInstagram": "Instagram",
    "channelWhatsapp": "WhatsApp",
    "channelBooking": "Booking platform",
    "profile": {
      "viewProfile": "View profile",
      "whatsappTitle": "WhatsApp connection",
      "instagramTitle": "Instagram connection",
      "sectionAssets": "Account & assets",
      "sectionTemplates": "Message templates",
      "templatesSubtitle": "Templates must be approved by Meta before use outside the 24-hour window.",
      "expand": "Expand panel",
      "collapse": "Collapse panel",
      "refresh": "Refresh",
      "disconnect": "Disconnect",
      "disconnectTitle": "Disconnect channel?",
      "disconnectBody": "This removes the channel connection for this studio. You can reconnect later from Settings.",
      "templatesPickerHint": "Approved templates can be selected from Chats when replying on WhatsApp.",
      "loading": "Loading connection details…",
      "pageName": "Facebook Page",
      "pageId": "Page ID",
      "emailTitle": "Email connection",
      "providerLabel": "Provider",
      "providerGoogle": "Google",
      "providerMicrosoft": "Microsoft",
      "grantHealthWarning": "Your email connection expired. Reconnect to send and receive messages.",
      "errorLoadEmail": "Could not load email connection details."
    },
    "whatsapp": {
      "templates": {
        "create": "Create message template",
        "editTitle": "View and edit template",
        "editSubtitleEditable": "Update category or message body, then save. Meta will review the template again automatically.",
        "editSubtitleReadOnly": "This template is pending or approved and cannot be edited here.",
        "saveChanges": "Save changes",
        "saveSuccess": "Template saved and sent to Meta for review",
        "saveFailed": "Could not save template changes",
        "nameLocked": "Template name cannot be changed after creation",
        "submit": "Submit for approval",
        "testSend": "Test send",
        "preview": "Preview",
        "reviewPresets": "Recommended OktoGo templates",
        "testSendDialogTitle": "Send test message",
        "testSendSuccess": "Test message queued",
        "testSendFailed": "Test send failed",
        "createSuccess": "Template submitted to Meta",
        "createFailed": "Could not create template",
        "submitFailed": "Could not submit template for approval",
        "emptyProfile": "No message templates yet. Create one to send messages outside the 24-hour window.",
        "nameHint": "Lowercase letters, numbers, and underscores only",
        "nameInvalid": "Template name must be lowercase with letters, numbers, and underscores",
        "bodyRequired": "Template body is required",
        "bodyLabel": "Message body",
        "bodyPlaceholderHint": "Use {{1}}, {{2}}, … for dynamic values. Meta requires a sample for each variable.",
        "variableSamplesTitle": "Variable samples for Meta review",
        "variableSamplesHint": "Provide realistic example text for each variable. Meta rejects templates without samples (INVALID_FORMAT).",
        "variableSampleLabel": "Sample for {{index}}",
        "variableSamplePlaceholder": "Example value shown to Meta reviewers",
        "variableSamplesRequired": "Enter a sample value for every variable in the message body",
        "variableSampleMetaSlot": "Meta placeholder {{index}}",
        "variables": {
          "customerName": "Customer name",
          "studioName": "Studio name",
          "managerName": "Manager name",
          "className": "Class or session name",
          "classDate": "Class date",
          "classTime": "Class time",
          "followUpNote": "Follow-up message",
          "visitDatetime": "Visit date and time",
          "bookingDate": "Booking date",
          "bookingTime": "Booking time",
          "newDate": "New date",
          "newTime": "New time",
          "spotDate": "Spot date",
          "spotTime": "Spot time",
          "handoffNote": "Handoff message",
          "reengagementNote": "Re-engagement message",
          "rebookOffer": "Rebook offer",
          "membershipType": "Membership type",
          "renewalDate": "Renewal date",
          "propertyName": "Property name",
          "checkinDate": "Check-in date",
          "accessDetails": "Access details"
        },
        "testSendTo": "To (E.164 phone number)",
        "e164Invalid": "Enter a valid E.164 phone number (e.g. +14155552671)",
        "variableLabel": "Variable {{index}}",
        "delete": "Delete template",
        "deleteConfirmTitle": "Delete message template?",
        "deleteConfirmBody": "Delete \"{{name}}\" (status: {{status}})? This removes the template from Meta. You may need to create it again before sending messages outside the 24-hour window. OktoGo bootstrap templates may need to be re-created.",
        "deleteConfirmAction": "Delete template",
        "deleteSuccess": "Template deleted",
        "deleteFailed": "Could not delete template",
        "deleteNotDeletable": "This template cannot be deleted while it is pending or approved",
        "rejectionReason": "Rejection reason: {{reason}}",
        "presetClassReminder": "Class reminder with booking details (T-1 demo send)",
        "presetSupportFollowup": "Manager follow-up after AI handoff (T-2)",
        "presetTrialWelcome": "First-trial welcome — create/submit demo (T-3)",
        "catalog": {
          "classReminder": "Class or session reminder with date, time, and rebooking prompt",
          "supportFollowup": "Manager follow-up after AI copilot handoff or open inquiry",
          "trialWelcome": "First-trial or intro visit confirmation",
          "bookingConfirmed": "Booking confirmation after studio or AI books a slot",
          "bookingCancelled": "Booking cancellation notice with rebook option",
          "bookingRescheduled": "Booking date or time change",
          "waitlistAvailable": "Waitlist spot opened — invite customer to claim",
          "aiHandoff": "AI copilot escalated — human manager will reply",
          "inactiveReengagement": "Re-engage customer outside the 24-hour messaging window",
          "classCancelledStudio": "Studio cancelled a class or session",
          "membershipRenewal": "Membership or package renewal reminder",
          "stayCheckin": "Short-term rental check-in details and arrival help"
        }
      }
    },
    "instagram": {
      "humanAgentEligible": "Human Agent eligible",
      "humanAgentEligibleYes": "Yes",
      "humanAgentEligibleNo": "No"
    },
    "webhookPending": {
      "banner": "Connection active, but message delivery is pending verification",
      "reverify": "Re-verify webhook"
    },
    "whatsappActivate": {
      "banner": "WhatsApp is linked but not activated for messaging. Enter your 6-digit WhatsApp two-step verification PIN to finish setup.",
      "cta": "Complete activation",
      "title": "Complete WhatsApp activation",
      "description": "Enter the 6-digit PIN from WhatsApp two-step verification (Business Manager or the WhatsApp app). Do not change the PIN in WhatsApp Manager first — complete activation here so Meta registers the number with Cloud API. OktoGo does not store this PIN.",
      "pinLabel": "WhatsApp PIN",
      "pinPlaceholder": "6-digit PIN",
      "submit": "Activate",
      "cancel": "Cancel",
      "close": "Close",
      "success": "WhatsApp is activated for messaging.",
      "errors": {
        "pinInvalid": "PIN does not match WhatsApp two-step verification.",
        "reverificationRequired": "WhatsApp requires phone re-verification in Meta Business.",
        "rateLimited": "Too many attempts. Try again in up to 72 hours.",
        "generic": "Activation failed. Please try again.",
        "network": "Network error during activation."
      }
    },
    "firstTrialReadiness": {
      "title": "First-trial links readiness",
      "precedenceNote": "Edit these links in Studio Data → General. Connections shows readiness only.",
      "scenario1Ready": "Scenario 1 ready: registration/trial link configured",
      "scenario1Missing": "Scenario 1: registration/trial link missing",
      "scenario2Ready": "Scenario 2 ready: Eversports studio page configured",
      "scenario2Missing": "Scenario 2: Eversports studio page missing",
      "ctaStudioData": "Open Studio Data",
      "ctaAiSettings": "Open AI Settings"
    }
  },
  "archive": {
    "permissionToast": "You don't have permission to view Archive.",
    "restoreError": "Could not restore",
    "studioLimitReached": "Maximum 5 active studios. Archive one before restoring this studio.",
    "conversationRestored": "Conversation restored",
    "studioRestored": "Studio restored",
    "deleteError": "Could not delete",
    "deleteBlockedFeedback": "This conversation has AI feedback and cannot be permanently deleted. It stays archived.",
    "conversationDeleted": "Conversation permanently deleted",
    "studioDeleted": "Studio permanently deleted",
    "loadError": "Could not load archive.",
    "empty": "No archived objects yet",
    "noFilterResults": "No results for current filters.",
    "columnName": "Name",
    "columnType": "Type",
    "columnStudio": "Studio",
    "columnArchived": "Archived",
    "columnActions": "Actions",
    "channelTitle": "Channel",
    "channelWhatsapp": "WhatsApp",
    "channelInstagram": "Instagram",
    "channelUnknown": "—",
    "restore": "Restore",
    "deleteForever": "Delete forever",
    "purgeTitle": "Delete forever",
    "purgeBodyConversation": "Permanently delete this archived conversation ({{name}})? This cannot be undone.",
    "purgeBodyStudio": "Permanently delete this archived studio ({{name}})? This cannot be undone.",
    "purgeDangerConversation": "All messages and metadata for this conversation will be removed from the database.",
    "purgeDangerStudio": "The studio and its related data will be permanently removed from the database.",
    "loadMore": "Load more",
    "purgeConversationTitle": "Delete conversation forever?",
    "purgeConversationBody": "This removes the archived chat permanently. This cannot be undone.",
    "purgeStudioTitle": "Delete studio forever?",
    "purgeStudioBody": "Type the studio name to confirm permanent deletion.",
    "searchAria": "Search archive by name",
    "filter": {
      "panelTitle": "Filter",
      "panelDescription": "Narrow archived chats and studios by type, studio, and when they were archived.",
      "filterArchiveAria": "Filter archive",
      "sectionType": "Type",
      "sectionStudio": "Studio",
      "sectionArchivedAgo": "Archived ago",
      "typeAll": "All",
      "typeChat": "Chat",
      "typeStudio": "Studio",
      "allStudios": "All studios",
      "filterByStudioAria": "Filter by studio",
      "tagTypeChat": "Type: Chat",
      "tagTypeStudio": "Type: Studio",
      "tagStudio": "Studio: {{name}}",
      "tagArchived": "Archived: {{label}}",
      "studioFallback": "Studio"
    },
    "time": {
      "all": "All dates",
      "today": "Today",
      "yesterday": "Yesterday",
      "this_week": "This week",
      "last_week": "Last week",
      "this_month": "This month",
      "last_month": "Last month",
      "this_year": "This year"
    },
    "rowType": {
      "conversation": "Chat",
      "studio": "Studio"
    }
  },
  "studio": {
    "scopeAllStudiosRejected": "All Studios is not available for your account. Switched to a single studio.",
    "scopeSwitchFailed": "Could not update studio scope. Please try again."
  },
  "vertical": {
    "fitness": {
      "dataSectionNavLabel": "Studio Data",
      "dataSectionPageTitle": "Studio Data",
      "dataSectionReadOnlyNoun": "Studio Data",
      "dataSectionAddUnitExpandedLabel": "Add new studio",
      "dataSectionListColumnTitle": "Studios",
      "dataSectionMobileDescription": "Manage your studio information, FAQs, policies, and class schedule.",
      "aggregateScopeLabel": "All Studios",
      "selectScopeActionLabel": "Select Studio",
      "overlaySelectScopeMessage": "Switch to a specific studio to edit settings.",
      "teamInviteUnitsMulti": "Studios (select one or more)",
      "teamInviteUnitsSingle": "Studio",
      "teamInviteNoUnitsAvailable": "No studios are available for invitations with your account.",
      "teamInviteSelectAtLeastOne": "Select at least one studio.",
      "chatsConversationUnitLabel": "Studio",
      "chatsIdentificationMatchingCopy": "We're matching this contact to a studio member. This may take a moment.",
      "agentTraceSelectScopeMessage": "Select a studio to load traces.",
      "agentTraceLoadErrorNeedsScope": "Select a studio and enter a valid agent run UUID.",
      "agentTraceNoAuditEventsForUnit": "No audit events in this time window for this studio.",
      "aiSettingsContactOwnerReadOnly": "Contact your studio owner to change AI settings.",
      "onboardingBookingConnectTitle": "Connect Booking Platform",
      "onboardingBookingConnectSubtitle": "Connect your booking platform to sync classes, schedules, and memberships.",
      "onboardingBookingUsernamePlaceholder": "you@studio.com",
      "dashboardStatConversationsToday": "Conversations today",
      "dashboardStatHandledByAiPct": "Handled by AI %",
      "dashboardStatBookingsMade": "Bookings made",
      "dashboardStatAvgFirstResponse": "Avg first response",
      "chatsMockJoinedBookingLine": "Joined Eversports",
      "chatsMockMembershipHeading": "Membership",
      "chatsMockScheduleHeading": "Classes",
      "chatsCustomerPanelPlatformsHeading": "Platforms",
      "chatsCustomerPanelAggregatorLabel": "Aggregator",
      "chatsCustomerPanelNetworkLabel": "Network",
      "chatsCustomerPanelPrimaryPlatformIdLabel": "Eversports customer ID",
      "chatsCustomerPanelSecondaryPlatformIdLabel": "myClubs member ID",
      "chatsCustomerPanelAggregatorIdLabel": "Aggregator ID",
      "chatsCustomerPanelCustomerRecordIdLabel": "Customer ID",
      "chatsCustomerPanelConversationRefLabel": "Conversation",
      "chatsUnmatchedNotFoundTitle": "Customer not found in Eversports",
      "chatsUnmatchedNotFoundBody": "This contact doesn't match any known member. Manual lookup may be required.",
      "chatsIdentificationInProgressTitle": "Customer identification in progress",
      "chatsNoMembershipRecordTitle": "No membership record",
      "chatsNoMembershipRecordBody": "This looks like a general inquiry or potential new member.",
      "visionAgentTraceButtonLabel": "Vision agent trace",
      "pageScopeDescription": "Dashboard, chats, and studio data below follow the studio you select here. Use “All Studios” for an aggregate view where available.",
      "connectionsTableUnitColumn": "Studio",
      "settingsAccountVerticalTitle": "Product vertical",
      "settingsAccountVerticalHint": "Fitness keeps studio and class wording. Short-term rental switches copy and listing-channel options to a property context."
    },
    "str": {
      "dataSectionNavLabel": "Property Data",
      "dataSectionPageTitle": "Property Data",
      "dataSectionReadOnlyNoun": "Property Data",
      "dataSectionAddUnitExpandedLabel": "Add new property",
      "dataSectionListColumnTitle": "Properties",
      "dataSectionMobileDescription": "Manage your property information, FAQs, policies, and guest-facing details.",
      "aggregateScopeLabel": "All Properties",
      "selectScopeActionLabel": "Select Property",
      "overlaySelectScopeMessage": "Switch to a specific property to edit settings.",
      "teamInviteUnitsMulti": "Properties (select one or more)",
      "teamInviteUnitsSingle": "Property",
      "teamInviteNoUnitsAvailable": "No properties are available for invitations with your account.",
      "teamInviteSelectAtLeastOne": "Select at least one property.",
      "chatsConversationUnitLabel": "Property",
      "chatsIdentificationMatchingCopy": "We're matching this contact to a guest or reservation. This may take a moment.",
      "agentTraceSelectScopeMessage": "Select a property to load traces.",
      "agentTraceLoadErrorNeedsScope": "Select a property and enter a valid agent run UUID.",
      "agentTraceNoAuditEventsForUnit": "No audit events in this time window for this property.",
      "aiSettingsContactOwnerReadOnly": "Contact your property owner to change AI settings.",
      "onboardingBookingConnectTitle": "Connect listing channel",
      "onboardingBookingConnectSubtitle": "Connect your listing or reservation platform to sync availability, reservations, and guest details.",
      "onboardingBookingUsernamePlaceholder": "you@example.com",
      "dashboardStatConversationsToday": "Conversations today",
      "dashboardStatHandledByAiPct": "Handled by AI %",
      "dashboardStatBookingsMade": "Reservations confirmed",
      "dashboardStatAvgFirstResponse": "Avg first response",
      "chatsMockJoinedBookingLine": "Primary booking channel",
      "chatsMockMembershipHeading": "Guest program",
      "chatsMockScheduleHeading": "Upcoming stays",
      "chatsCustomerPanelPlatformsHeading": "Channels",
      "chatsCustomerPanelAggregatorLabel": "Primary channel",
      "chatsCustomerPanelNetworkLabel": "Partner",
      "chatsCustomerPanelPrimaryPlatformIdLabel": "Guest ID",
      "chatsCustomerPanelSecondaryPlatformIdLabel": "Reservation reference",
      "chatsCustomerPanelAggregatorIdLabel": "Source reference",
      "chatsCustomerPanelCustomerRecordIdLabel": "Guest record ID",
      "chatsCustomerPanelConversationRefLabel": "Conversation",
      "chatsUnmatchedNotFoundTitle": "Guest not found in booking system",
      "chatsUnmatchedNotFoundBody": "This contact doesn't match a known guest or reservation. Manual lookup may be required.",
      "chatsIdentificationInProgressTitle": "Guest identification in progress",
      "chatsNoMembershipRecordTitle": "No reservation on file",
      "chatsNoMembershipRecordBody": "This looks like a general inquiry or a potential new booking.",
      "visionAgentTraceButtonLabel": "Vision agent trace",
      "pageScopeDescription": "Dashboard, chats, and property data below follow the property you select here. Use “All Properties” for an aggregate view where available.",
      "connectionsTableUnitColumn": "Property",
      "settingsAccountVerticalTitle": "Product vertical",
      "settingsAccountVerticalHint": "Short-term rental uses property and guest wording with listing-channel integrations. Fitness uses studio and class wording."
    }
  },
  "notFound": {
    "code": "404",
    "title": "Oops! Page not found",
    "home": "Return to Home"
  },
  "routeErrorBoundary": {
    "summary": "This page failed to load. Please try reloading.",
    "reload": "Reload page"
  },
  "setupNotComplete": {
    "title": "Studio setup not complete",
    "body": "Your studio owner hasn't finished setting up the studio yet. You'll get access once setup is complete."
  },
  "ai": {
    "readOnlyContactOwner": "Contact your studio owner to change AI settings.",
    "readOnlyContactOwnerStr": "Contact your property owner to change AI settings.",
    "sectionReplies": "Replies by topic",
    "regimeLockedBanner": "During the pilot, Auto-send stays off for all topics. Contact OktoGo to unlock the Auto/Manual switcher.",
    "toastRegimeLocked": "Auto-send is locked for this pilot studio. Contact OktoGo to unlock.",
    "sectionTone": "Conversation tone",
    "sectionLearn": "Learn from team replies",
    "sectionAttribution": "Labels for automated AI replies",
    "sectionEscalation": "Escalation Rules",
    "sectionEscalationHint": "When to flag a conversation for your review (soft escalation). The AI still drafts a reply — you approve or edit before it is sent. Hard takeover — when a customer asks for a human, or the AI cannot finish safely — is a separate safety seatbelt and is not controlled by these rules.",
    "sectionSessionNudge": "Messaging session nudges",
    "sectionSessionNudgeHint": "Optional short message to the customer before the WhatsApp free-form window closes. Only runs when server automation is enabled; pending AI drafts skip the send.",
    "escalationStillDraftsNote": "Whether flagged by these rules or by a hard safety handoff, escalated conversations wait for your approval — except identity confirmation messages, which are always sent so the customer can start. The AI keeps drafting suggested replies so you never start from zero.",
    "save": "Save",
    "saving": "Saving…",
    "saved": "AI settings saved.",
    "saveError": "Could not save AI settings.",
    "loadError": "Could not load AI settings.",
    "bookingRequiredTitle": "Connect booking platform first",
    "bookingRequiredBody": "Turning on auto-handle for booking topics works best when a booking platform is connected in Settings → Channels.",
    "confirmEnableBooking": "Enable without connected booking?",
    "enable": "Enable",
    "useCase": {
      "booking": "New Booking",
      "rebook": "Rebooking",
      "cancel": "Cancellation",
      "info": "General Info",
      "schedule": "Schedule Inquiry",
      "trial": "Trial / first visit",
      "account": "Account / Membership",
      "campaignLead": "Campaign lead"
    },
    "firstTrial": {
      "helper": {
        "title": "First trial / first visit — Copilot picks the path for you:",
        "scenario1": "No booking platform account → registration / trial link",
        "scenario2": "Has an Eversports account, new at your studio → Eversports studio page; booking stays draft until they are linked at your studio",
        "scenario2Booking": "After link-up, normal booking rules apply (New booking row)"
      },
      "readiness": {
        "title": "First-trial links readiness",
        "precedenceNote": "Edit these links in Studio Data → General. Connections shows readiness only.",
        "scenario1Ready": "Scenario 1 ready: registration/trial link configured",
        "scenario1Missing": "Scenario 1: registration/trial link missing",
        "scenario2Ready": "Scenario 2 ready: Eversports studio page configured",
        "scenario2Missing": "Scenario 2: Eversports studio page missing",
        "ctaStudioData": "Open Studio Data",
        "ctaConnections": "Open Connections"
      }
    },
    "tone": {
      "friendly": {
        "label": "Friendly",
        "desc": "Warm, approachable, uses emojis. Starts informal (du). Switches to Sie if the customer or the team writes a clear formal you."
      },
      "professional": {
        "label": "Formal",
        "desc": "Polished, formal register, no emojis. Starts with the formal you (German Sie). Switches to informal if the customer or the team later writes a clear du."
      },
      "casual": {
        "label": "Casual",
        "desc": "Relaxed and short. Starts informal (du). Switches to Sie if the customer or the team writes a clear formal you."
      }
    },
    "repliesByTopicDesc": "Auto: after checks pass and the customer confirms when required (e.g. rebooking), AI may execute the action and send a verified success reply. Manual: after those same checks and customer confirm, AI waits for your approval before any platform change and before the success reply.",
    "repliesByTopicManualWait": "On Manual, these topics wait for your approval.",
    "repliesByTopicStillSentChip": "Still sent on their own: confirm who they are, already in the class, a later “it’s done”, a later “you’re verified”, and one “shall I cancel?” before you confirm. Nothing else.",
    "approveAllFirst": "Approve all messages first",
    "autoSendAllMessages": "Auto-send all messages",
    "bookingUseCaseHint": "Connect in Settings → Channels to use this with live schedule and booking data.",
    "toneSectionDesc": "Starting style for every conversation. The chat can become more formal or more informal if the customer or the team later uses a clear you-word.",
    "learnSectionDesc": "Lets the AI echo how your team writes — length, warmth, sign-off. One clear team Sie or du is enough for the next suggestion. The latest clear you-word from the customer or the team wins. With no clear example, the starting style above still applies.",
    "attributionSectionDesc": "Auto-sent AI replies always show this assistant name in Chats. You cannot turn labels off. WhatsApp and Instagram still use your connected business account — not this name.",
    "attributionDisplayNameLabel": "Assistant display name",
    "attributionDisplayNameHint": "Type only the name — AI is added automatically and cannot be removed. Example: Kai → Kai AI. WhatsApp and Instagram customers still see your connected business account.",
    "attributionDisplayNameErrorEmpty": "Enter a name for the assistant.",
    "attributionDisplayNameErrorToken": "Enter a name for the assistant.",
    "attributionDisplayNameAiSuffixTitle": "AI is always added after the name",
    "attributionDisplayNameAiSuffixSr": "AI is fixed after the name and cannot be edited.",
    "attributionDisplayNameSave": "Save assistant name",
    "attributionDisplayNameReset": "Reset to default",
    "attributionBackendPendingHint": "Saving is not available yet — the server still needs the assistant-name API. You can draft and validate the name here.",
    "attributionStudio": "Studio name",
    "attributionAi": "AI assistant",
    "attributionLockedHint": "Owner or manager can change this setting.",
    "toastAttributionSaved": "Assistant display name saved.",
    "toastAttributionBackendNotReady": "Cannot save yet — assistant display name is not available on the server.",
    "escalateAfterPrefix": "Escalate after",
    "escalateAfterSuffix": "failed attempts before flagging the conversation for your review.",
    "dialogEnableAutoSendTitle": "Enable auto-send",
    "dialogEnableAutoSendBody": "After required checks pass and the customer confirms when the flow requires it (e.g. rebooking cancel+book), the AI may execute this action and send a verified success reply without your approval. Manual mode waits for your approval before any platform change and before the success reply. Continue?",
    "dialogAutoSendAllTitle": "Auto-send all messages",
    "dialogAutoSendAllBody": "For booking, rebooking, and cancellation, the AI may execute only after required checks pass and the customer confirms when the flow requires it (rebooking = one confirm for cancel+book together). Manual mode waits for your approval before any platform change and before the success reply.",
    "enableAll": "Enable all",
    "toastSettingSaveError": "Could not save that setting.",
    "toastAttributionError": "Failed to update automated reply labels.",
    "toastEscalationError": "Failed to update escalation rules.",
    "sessionNudgeEnabled": "Enable automated session nudge",
    "sessionNudgeWhatsapp": "WhatsApp",
    "sessionNudgeInstagram": "Instagram (experimental)",
    "sessionNudgeOnlyEscalated": "Only when escalated",
    "sessionNudgeMaxOne": "At most one nudge per escalation",
    "sessionNudgeWhenRemaining": "Send when time remaining is at most",
    "sessionNudgeHours4": "4 hours",
    "sessionNudgeHours6": "6 hours",
    "sessionNudgeHours8": "8 hours",
    "sessionNudgePreview": "Default message preview",
    "sessionNudgePreviewMayDiffer": "The actual message may sound more or less formal per conversation, based on how your team writes to that customer.",
    "toastSessionNudgeError": "Could not save session nudge settings.",
    "toastAutoHandleError": "Failed to update auto-handle setting.",
    "toastAutoHandleRateLimited": "Too many updates at once. Please try again in a moment.",
    "toastAutoHandleRateLimitedWithRetry": "Too many updates at once. Try again in about {{seconds}} seconds.",
    "toastToneError": "Failed to update tone.",
    "toastGenericUpdateError": "Could not update settings.",
    "connectBookingFirst": "Connect a booking platform in Settings → Channels first.",
    "connectListingFirst": "Connect a listing channel in Settings → Channels first.",
    "campaignLeadNotReady": "Set up a ready campaign in Studio Data → Campaigns before turning this on.",
    "campaignLead": {
      "hintNoCampaign": "No campaign configured yet",
      "hintNotReady": "Campaign not ready yet",
      "hintEndDateMissing": "Campaign is missing an end date",
      "hintEndingSoon": "Campaign ends {{date}}",
      "hintExpired": "Last campaign has expired",
      "hintSummaryActive": "{{count}} active",
      "hintSummaryScheduled": "{{count}} scheduled",
      "hintSummaryNotReady": "{{count}} not ready",
      "hintSummaryExpired": "{{count}} expired",
      "ctaOpenCampaigns": "Open Campaigns"
    },
    "groupBooking": {
      "sectionTitle": "Group bookings",
      "sectionDesc": "Copilot detects group and corporate requests from the conversation and triages them safely instead of booking single-seat slots.",
      "tabsAriaLabel": "Group bookings settings sections",
      "tabBasics": "Basics",
      "tabPolicies": "Policies",
      "lockAria": "Owner-only setting",
      "enableLabel": "Enable group bookings for this studio",
      "enableHint": "When on, group-shaped conversations get a pending draft and a manager checklist instead of a normal auto-send.",
      "fulfillmentLabel": "How to handle group bookings",
      "fulfillment": {
        "manual": "Manual",
        "semiAutomated": "Semi-automated",
        "fullyAutomated": "Fully automated",
        "manualDesc": "Copilot drafts replies and shows a roster checklist; you book in your booking platform manager by hand.",
        "semiAutomatedDesc": "Copilot drafts replies; after you approve, it can run safe roster steps in your booking platform manager when ready.",
        "fullyAutomatedDesc": "Pilot only. Copilot may run safe roster steps in your booking platform manager before approval; you supervise and intervene anytime.",
        "requiresConnection": "Semi-automated and Fully automated need a connected booking platform. Connect one in Settings → Channels."
      },
      "maxPartyLabel": "Largest group Copilot may automate",
      "maxPartyHint": "Applies only to Semi-automated and Fully automated. Larger groups stay manual. Range 2–50.",
      "escalationTargetLabel": "Notify for handoff",
      "escalationTargetHint": "Who is alerted when a group booking needs a human. Delivery: Chats while you're in Copilot; mobile push when away (per Account → Notifications).",
      "escalationTarget": {
        "owner": "Studio owner",
        "allChatMembers": "All managers with chat access"
      },
      "saveButton": "Save group booking policy",
      "toastSaved": "Group booking policy saved.",
      "toastSaveError": "Could not save group booking policy.",
      "toastEndpointUnavailable": "Group booking policy API is not available on this server yet. Settings will apply when your administrator deploys the backend update.",
      "toastLoadUnavailable": "AI settings could not be loaded from the server.",
      "endpointUnavailableHint": "Saving is disabled until the policy endpoint is available."
    },
    "escalation": {
      "no_match": "No intent matched",
      "sentiment": "Negative sentiment detected",
      "repeat": "Customer repeats request 3+ times",
      "explicit": "Customer asks for human"
    }
  },
  "channelConnect": {
    "header": {
      "connectInstagram": "Connect Instagram",
      "connectWhatsapp": "Connect WhatsApp",
      "selectStudioTooltip": "Select a single studio to connect channels"
    },
    "wizard": {
      "yes": "Yes",
      "no": "No",
      "continue": "Continue",
      "back": "Back",
      "cancel": "Cancel",
      "retry": "Retry",
      "learnMore": "Learn more",
      "instagram": {
        "title": "Connect Instagram",
        "introBody": "Connect your Instagram Professional account so Oktogo can manage customer DMs in Chats.",
        "q1": "Do you already have an Instagram Professional account (Business or Creator)?",
        "q1Yes": "Yes, I have a Professional account",
        "q1No": "No, I don't have one yet",
        "q1HelpTitle": "How to switch to a Professional account",
        "q1Help": "Open Instagram → Settings → Account → Switch to professional account, then choose Business or Creator.",
        "q2": "Is your Instagram account linked to a Facebook Page?",
        "q2Yes": "Yes, it's linked to a Facebook Page",
        "q2No": "No, it's not linked yet",
        "q2HelpTitle": "Link Instagram to a Facebook Page",
        "q2Help": "In Meta Business Suite or Facebook Page settings, connect your Instagram account to the Page that represents your studio.",
        "q3": "Is access to messages enabled for this Instagram account?",
        "q3Yes": "Yes, message access is enabled",
        "q3No": "No, it's not enabled yet",
        "q3HelpTitle": "Enable message access",
        "q3Help": "In the Instagram app: Settings → Messages → Message controls → allow access for connected tools.",
        "confirm": "Connect Instagram",
        "checkProfessional": "Instagram Professional account (Business or Creator)",
        "checkFacebookPage": "Instagram linked to a Facebook Page",
        "checkMessageAccess": "Message access enabled"
      },
      "whatsapp": {
        "title": "Connect WhatsApp",
        "introBody": "Connect WhatsApp Business so Oktogo can manage customer messages in Chats.",
        "q1": "Do you have a WhatsApp Business account (or a number eligible for the Business API)?",
        "q1Yes": "Yes, I have WhatsApp Business",
        "q1No": "No, I don't have it yet",
        "q1HelpTitle": "Set up WhatsApp Business",
        "q1Help": "Install WhatsApp Business on your studio phone or register a number with Meta for the Business API.",
        "q2": "Can you sign in with the Meta account that administers this business?",
        "q2Yes": "Yes, I have Meta admin access",
        "q2No": "No, I don't have admin access",
        "q2HelpTitle": "Meta business admin access",
        "q2Help": "You need admin access to the Meta Business account that owns the WhatsApp Business profile.",
        "confirm": "Connect WhatsApp",
        "checkBusiness": "WhatsApp Business account ready",
        "checkMetaAccess": "Meta business admin access confirmed"
      }
    },
    "oauth": {
      "successInstagram": "Instagram connected",
      "successInstagramHandle": "Connected as {{handle}}",
      "successWhatsapp": "WhatsApp connected",
      "webhookPending": "Instagram is connected, but message delivery is pending verification."
    },
    "errors": {
      "accessDenied": "You cancelled permissions. Try again when you are ready.",
      "connectFailed": "Connection failed. Please try again."
    },
    "email": {
      "connect": "Connect email",
      "connectTooltip": "Connect a shared mailbox for customer email in Chats (all studios)",
      "wizard": {
        "title": "Connect email",
        "introBody": "Connect your studio mailbox so Oktogo can manage customer email threads in Chats.",
        "providerGoogle": "Google (Gmail / Google Workspace)",
        "providerMicrosoft": "Microsoft (Outlook / Microsoft 365)",
        "prereqsTitle": "Before you connect",
        "prereqsBody": "Use an account you can sign in to with Google or Microsoft. You need permission to connect this mailbox for your business.",
        "confirm": "Connect email",
        "defaultStudioLabel": "Default studio for new email threads",
        "defaultStudioHint": "New inbound email is routed to this studio until the customer clarifies otherwise.",
        "openChats": "Open Chats"
      },
      "oauth": {
        "success": "Email connected",
        "successAddress": "Connected as {{address}}"
      }
    }
  },
  "whatsappSignup": {
    "title": "Connect WhatsApp Business",
    "description": "Authorize OktoGo to manage messages on behalf of your WhatsApp Business account.",
    "connectButton": "Connect with Meta",
    "fbFlow": "Complete the sign-in in the Meta window…",
    "saving": "Saving connection…",
    "successRedirect": "WhatsApp connected! Redirecting…",
    "hint": "You will sign in to Meta and select your WhatsApp Business Account.",
    "metaCompleteHint": "Meta authorization is complete. Enter your PIN below to finish connecting in OktoGo.",
    "pinDescription": "Enter the 6-digit PIN from WhatsApp two-step verification to register this number with Meta Cloud API.",
    "pinLabel": "WhatsApp PIN",
    "pinPlaceholder": "6-digit PIN",
    "pinSubmit": "Activate and connect",
    "retry": "Try again",
    "errors": {
      "generic": "Something went wrong.",
      "missingConfig": "Meta app configuration is missing. Check VITE_META_APP_ID and VITE_META_ES_CONFIG_ID.",
      "phoneAlreadyConnected": "This phone number is already connected to another studio on your account.",
      "metaExchangeFailed": "Meta could not exchange the login code. Confirm server redirect URI matches this site.",
      "wabaDiscoveryFailed": "Could not find your WhatsApp Business Account. Ensure you have a WABA with a registered phone number.",
      "studioContextRequired": "Select a specific studio before connecting WhatsApp, then try again from Settings or Chats.",
      "connectFailed": "Failed to save the WhatsApp connection. Please try again.",
      "sessionExpired": "Your session expired. Log in again, then reconnect WhatsApp from Settings.",
      "network": "Network error while saving connection. Please try again.",
      "cancelled": "Meta sign-in was cancelled. Please try again.",
      "sdkLoadFailed": "Could not load the Meta sign-in SDK. Please refresh and try again.",
      "activationRequired": "Enter your 6-digit WhatsApp PIN to activate messaging.",
      "pinInvalid": "PIN does not match WhatsApp two-step verification.",
      "reverificationRequired": "WhatsApp requires phone re-verification in Meta Business.",
      "registerRateLimited": "Too many registration attempts. Try again in up to 72 hours.",
      "registerFailed": "WhatsApp phone registration failed. Please try again."
    }
  },
  "whatsappTemplates": {
    "title": "Message templates",
    "subtitle": "Approved templates from your connected WhatsApp Business account (required for messages outside the 24-hour window).",
    "loading": "Loading templates…",
    "empty": "No message templates found for this account.",
    "errorLoad": "Could not load message templates.",
    "retry": "Retry",
    "colName": "Name",
    "colLanguage": "Language",
    "colCategory": "Category",
    "colStatus": "Status"
  },
  "whatsappSetup": {
    "title": "Connected WhatsApp account",
    "subtitle": "Live details from your WhatsApp Business account (WABA), phone number, verification, and webhook subscription.",
    "loading": "Loading account details…",
    "errorLoad": "Could not load WhatsApp account details.",
    "retry": "Retry",
    "wabaId": "WABA ID",
    "phoneNumber": "Phone number",
    "displayName": "Display name",
    "phoneVerification": "Phone verification",
    "nameVerification": "Display name verification",
    "webhookSubscription": "Webhook subscription",
    "webhookSubscribed": "Subscribed",
    "webhookNotSubscribed": "Not subscribed",
    "webhookAppId": "Meta app ID",
    "webhookFields": "Subscribed fields"
  },
  "onboarding": {
    "shell": {
      "stepOf": "Step {{current}} of {{total}}",
      "exitAria": "Exit setup",
      "exitTitle": "Save progress and continue later?",
      "exitDescription": "Your progress will be saved. You can resume setup the next time you log in.",
      "cancel": "Cancel",
      "saveAndExit": "Save & Exit"
    },
    "instagram": {
      "title": "Connect Instagram",
      "description": "Connect your Instagram Business account so Oktogo can manage customer messages.",
      "errorConnect": "Instagram connection failed. Please try again.",
      "connected": "Connected",
      "connectButton": "Connect Instagram",
      "retry": "Retry",
      "continue": "Continue",
      "skipLater": "I'll do this later",
      "testErrorEnable": "(Test: enable error mode)",
      "testErrorDisable": "(Test: disable error mode)"
    },
    "whatsapp": {
      "title": "Connect WhatsApp Business",
      "description": "Connect your WhatsApp Business account so Oktogo can manage customer messages.",
      "errorConnect": "WhatsApp connection failed. Please try again.",
      "connected": "Connected",
      "connectButton": "Connect WhatsApp",
      "retry": "Retry",
      "continue": "Continue",
      "skipLater": "I'll do this later",
      "back": "Back"
    },
    "booking": {
      "title": "Connect Booking Platform",
      "description": "Connect your booking platform to sync classes, schedules, and memberships.",
      "errorCredentials": "Invalid credentials or platform unavailable. Please retry.",
      "connected": "Connected",
      "continue": "Continue",
      "platformLabel": "Booking Platform",
      "usernameLabel": "Username / Email",
      "passwordLabel": "Password",
      "skipLater": "I'll do this later",
      "disclosure": "We securely store your credentials to sync bookings and schedules. Your password is encrypted and only accessible to Oktogo's secure servers.",
      "testConnection": "Test Connection",
      "back": "Back",
      "testErrorEnable": "(Test: enable error mode)",
      "testErrorDisable": "(Test: disable error mode)",
      "manualCodeLabel": "Additional sign-in code",
      "manualCodeHint": "Eversports sent a one-time code (SMS, email, or authenticator). Enter it and run the test again.",
      "manualCodeRequiredTitle": "Manual verification required"
    },
    "twoFa": {
      "title": "Set Up 2FA Credentials",
      "description": "Eversports requires 2FA. Provide your credentials so Oktogo can authenticate on your behalf.",
      "errorVerify": "2FA verification failed – check your credentials and retry.",
      "verified": "2FA Verified",
      "verifiedHint": "Eversports authentication ready",
      "continue": "Continue",
      "methodLabel": "2FA Method",
      "methodAuthenticator": "Authenticator App",
      "methodSms": "SMS",
      "methodBackup": "Backup Code",
      "secretLabel": "2FA Secret / Backup Code",
      "disclosure": "Your 2FA credentials are encrypted and stored solely to allow Oktogo to authenticate with Eversports on your behalf. They are never shared or exposed.",
      "skipLater": "I'll do this later",
      "back": "Back",
      "testButton": "Test 2FA",
      "syncLimitedHint": "Sync may be limited until 2FA is provided",
      "testErrorEnable": "(Test: enable error mode)",
      "testErrorDisable": "(Test: disable error mode)"
    },
    "email": {
      "title": "Connect email",
      "description": "Connect your studio mailbox so Oktogo can manage customer email threads in Chats.",
      "connectButton": "Connect email",
      "skipLater": "I'll do this later",
      "back": "Back",
      "continue": "Continue",
      "connected": "Email connected",
      "connectedHint": "Your mailbox is ready for customer email in Chats.",
      "optionalHint": "Optional — you can connect email later from Settings."
    },
    "messaging": {
      "title": "Connect messaging channels",
      "description": "Connect Instagram and WhatsApp so Oktogo can manage customer messages in Chats.",
      "continue": "Continue",
      "skipLater": "I'll do this later",
      "back": "Back",
      "optionalHint": "Optional — you can connect these later from Settings → Channels."
    },
    "oauthCallbackLoading": "Connecting your account…",
    "errors": {
      "connectFailed": "Connection failed. Please try again."
    },
    "sync": {
      "titleRunning": "Syncing your studio data...",
      "titleDone": "Sync complete!",
      "titleSkipped": "You're all set for now",
      "hintRunning": "This will only take a moment.",
      "hintDone": "Your studio data has been imported successfully.",
      "hintSkipped": "No booking platform connected yet. You can connect later from Settings → Connections.",
      "stageClasses": "Fetching classes",
      "stageSchedule": "Syncing schedule",
      "stageMemberships": "Importing memberships",
      "stagePolicies": "Loading policies",
      "continueDashboard": "Continue to Dashboard",
      "completing": "Completing…",
      "summaryDemo": "{{classes}} classes, {{sessions}} upcoming sessions, {{tiers}} membership tiers imported.",
      "summaryReal": "{{classes}} classes and {{policies}} policies imported successfully.",
      "summaryGeneric": "Studio data imported successfully.",
      "summaryNoData": "Setup complete. You can connect a booking platform later from Settings → Connections.",
      "confirmTitle": "Confirm your public Eversports links",
      "confirmLinks": "Review Overview, Classes, and Prices URLs in Studio Data → General.",
      "confirmClassPrices": "Class descriptions and Pricing options sync from Eversports when available — check them under Studio Data → Classes.",
      "confirmHonesty": "Live Eversports prices win. Until class prices are available, Copilot can send the Prices page link — we never invent euro amounts.",
      "ctaReviewLinks": "Review section links in Studio Data",
      "ctaReviewClasses": "Review classes & prices in Studio Data",
      "titleError": "Sync failed",
      "hintError": "We couldn't import your studio data right now.",
      "errorBody": "Something went wrong while importing your data. You can retry now or finish setup and try again later from Settings.",
      "retry": "Retry sync",
      "back": "Back"
    }
  },
  "management": {
    "title": "Pilot Management",
    "subtitle": "Invite and control pilot customer onboarding.",
    "signOut": "Sign out",
    "login": {
      "title": "Management access",
      "subtitle": "Sign in with a platform admin account.",
      "emailLabel": "Email",
      "emailPlaceholder": "admin@oktogo.ai",
      "passwordLabel": "Password",
      "passwordPlaceholder": "Enter password",
      "signIn": "Sign in",
      "signingIn": "Signing in..."
    },
    "accept": {
      "title": "Accept pilot invite",
      "subtitle": "Create your secure password to start onboarding.",
      "email": "Email",
      "studio": "Studio",
      "expiresAt": "Expires at",
      "password": "Password",
      "passwordPlaceholder": "Create a password",
      "terms": "I accept the pilot onboarding terms",
      "submit": "Accept invite",
      "accepting": "Accepting invite..."
    },
    "createInvite": {
      "title": "Create pilot invite",
      "email": "Owner email",
      "fullName": "Owner full name (optional)",
      "studioName": "Studio name",
      "planId": "Plan id",
      "submit": "Create and send invite",
      "creating": "Creating invite..."
    },
    "invites": {
      "title": "Pilot invites",
      "loading": "Loading invites...",
      "empty": "No invites found for the selected filter.",
      "filterAria": "Filter invites by status",
      "filterAll": "All statuses",
      "columns": {
        "email": "Email",
        "studio": "Studio",
        "status": "Status",
        "expiresAt": "Expires at",
        "actions": "Actions"
      },
      "actions": {
        "resend": "Resend",
        "revoke": "Revoke"
      }
    },
    "status": {
      "pending": "Pending",
      "sent": "Sent",
      "accepted": "Accepted",
      "expired": "Expired",
      "revoked": "Revoked"
    },
    "vertical": {
      "fitness": "Fitness",
      "shortTermRental": "Short-term rental"
    },
    "messages": {
      "inviteCreated": "Pilot invite created ({{id}}).",
      "inviteActionSuccess": "Invite updated.",
      "inviteAccepted": "Invite accepted. Your account is now active."
    },
    "errors": {
      "login": "Unable to sign in.",
      "platformAdminRequired": "Platform admin access is required.",
      "loadInvites": "Unable to load invites.",
      "createInvite": "Unable to create invite.",
      "inviteAction": "Unable to update invite.",
      "invalidInviteToken": "Invite token is missing.",
      "loadInviteToken": "Unable to validate this invite token.",
      "acceptInvite": "Unable to accept invite."
    }
  },
  "compliance": {
    "aiDisclosure": {
      "auto": {
        "plain": "Hi! You are chatting with this studio's AI assistant. I can help with schedule, bookings, and quick questions - a team member can join anytime.",
        "named": "Hello {{channelFirstName}}. You are chatting with this studio's AI assistant. I can help with schedule, bookings, and quick questions - a team member can join anytime."
      },
      "manual": {
        "plain": "Hi! You are chatting with this studio's AI assistant. I drafted this reply and a team member checked it before sending, and you can ask for a person anytime.",
        "named": "Hello {{channelFirstName}}. You are chatting with this studio's AI assistant. I drafted this reply and a team member checked it before sending, and you can ask for a person anytime."
      }
    }
  }
}
